import { useCallback, useState, useEffect } from "react";
import { BrowserProvider, Contract, parseEther, ethers, parseUnits } from "ethers";
import QRCode from "react-qr-code";
import { saveAs } from "file-saver";
import { useSnackbar } from 'notistack';
import { useBlockedUsers } from './context/BlockedUsersProviderIPFS';
import KYCForm from './components/KYCForm';
import {
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Grid,
  Divider,
  Alert,
  Avatar,
  Chip,
  Stack,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';

import { useRef } from "react";

import AdminDashboard from "./components/admin/AdminDashboard";
import vaultArtifact from "./abis/PlatformVault.json";
import nftArtifact from "./abis/CarbonCreditNFT.json";
import carbonArtifact from "./abis/ETHCarbon.json";
import marketplaceArtifact from "./abis/CarbonCreditMarketplace.json";


const VAULT_ADDRESS = vaultArtifact["networks"][process.env.REACT_APP_NETWORK_ID]?.address;
const VAULT_ABI = vaultArtifact.abi;
const NFT_ABI = nftArtifact.abi;
const NFT_ADDRESS = nftArtifact["networks"][process.env.REACT_APP_NETWORK_ID]?.address;
const CARBON_ABI = carbonArtifact.abi;
const CARBON_ADDRESS = carbonArtifact["networks"][process.env.REACT_APP_NETWORK_ID]?.address;
const MARKETPLACE_ADDRESS = marketplaceArtifact["networks"][process.env.REACT_APP_NETWORK_ID]?.address;
const MARKETPLACE_ABI = marketplaceArtifact.abi;
const LOT_READ_RPC_URL = (
  process.env.REACT_APP_RPC_URL ||
  process.env.REACT_APP_NFT_AUDIT_RPC_URL ||
  "https://serenity.ing/cpr"
).trim();

// ---- Runtime address resolution helpers ------------------------------------
async function getNetVersion() {
  try {
    if (window.ethereum?.request) {
      const v = await window.ethereum.request({ method: 'net_version' });
      return v ? Number(v) : null;
    }
  } catch {}
  return null;
}

async function resolveArtifactAddress(provider, artifact, label) {
  const candidates = [];
  // 1) Env-provided network id
  const envId = process.env.REACT_APP_NETWORK_ID ? Number(process.env.REACT_APP_NETWORK_ID) : null;
  if (!Number.isNaN(envId) && envId) candidates.push(envId);
  // 2) Provider network chainId
  try {
    const net = await provider.getNetwork();
    if (net?.chainId) candidates.push(Number(net.chainId));
  } catch {}
  // 3) net_version (legacy network id)
  const netVer = await getNetVersion();
  if (netVer) candidates.push(netVer);

  // Dedupe
  const tried = new Set();
  for (const id of candidates) {
    const key = String(id);
    if (tried.has(key)) continue;
    tried.add(key);
    const addr = artifact?.networks?.[key]?.address;
    if (!addr) continue;
    try {
      const code = await provider.getCode(addr);
      if (code && code !== '0x') {
        console.log(`[addr-resolve] ${label} resolved networks[${key}] -> ${addr}`);
        return addr;
      } else {
        console.warn(`[addr-resolve] ${label} networks[${key}] -> ${addr} has no code`);
      }
    } catch (e) {
      console.warn(`[addr-resolve] ${label} code check failed at ${addr}: ${e.message}`);
    }
  }
  return null;
}

function looksLikeAddress(addr) {
  return typeof addr === 'string' && addr.length === 42 && addr.startsWith('0x');
}

function supportsPausedMethod(contractInstance) {
  try {
    contractInstance?.interface?.getFunction("paused");
    return true;
  } catch {
    return false;
  }
}

function supportsPauseEvents(contractInstance) {
  try {
    contractInstance?.interface?.getEvent("Paused");
    contractInstance?.interface?.getEvent("Unpaused");
    return true;
  } catch {
    return false;
  }
}

// ---- IPFS Gateway Helpers (CORS / Fallback) ---------------------------------
// Primary gateway first; others act as fallback if CORS blocked or 5xx returned.
const IPFS_GATEWAYS = [
  'https://serenity.ing/ipfs/',
  'https://cloudflare-ipfs.com/ipfs/',
  'https://ipfs.io/ipfs/',
  'https://gateway.pinata.cloud/ipfs/'
];

function resolveIpfsUrl(uri, gatewayBase = IPFS_GATEWAYS[0]) {
  if (!uri) return null;
  const path = uri.startsWith('ipfs://') ? uri.slice(7) : uri.replace(/^https?:\/\/[^/]+\/ipfs\//, '');
  return gatewayBase + path;
}

function formatUnitsRounded(amountWei, decimals = 18, maxFracDigits = 5) {
  try {
    const s = ethers.formatUnits(amountWei ?? 0n, decimals);
    if (!s.includes('.')) return s;
    const [intPart, fracRaw] = s.split('.');
    if (maxFracDigits <= 0) return intPart;

    const frac = (fracRaw || '').padEnd(maxFracDigits + 1, '0');
    const keep = frac.slice(0, maxFracDigits);
    const next = frac[maxFracDigits] || '0';

    // round half-up
    if (next < '5') {
      return `${intPart}.${keep}`.replace(/\.?0+$/, '');
    }

    // increment keep as a decimal string
    let carry = 1;
    const chars = keep.split('');
    for (let i = chars.length - 1; i >= 0; i--) {
      const d = (chars[i].charCodeAt(0) - 48) + carry;
      if (d >= 10) {
        chars[i] = '0';
        carry = 1;
      } else {
        chars[i] = String.fromCharCode(48 + d);
        carry = 0;
        break;
      }
    }

    let newInt = intPart;
    if (carry === 1) {
      // increment integer part (string-safe)
      const intChars = newInt.split('');
      let c = 1;
      for (let i = intChars.length - 1; i >= 0; i--) {
        const d = (intChars[i].charCodeAt(0) - 48) + c;
        if (d >= 10) {
          intChars[i] = '0';
          c = 1;
        } else {
          intChars[i] = String.fromCharCode(48 + d);
          c = 0;
          break;
        }
      }
      if (c === 1) intChars.unshift('1');
      newInt = intChars.join('');
    }

    const out = `${newInt}.${chars.join('')}`;
    return out.replace(/\.?0+$/, '');
  } catch {
    return String(amountWei ?? 0n);
  }
}

function upsertAttribute(attributes, trait_type, value) {
  const next = Array.isArray(attributes) ? [...attributes] : [];
  const idx = next.findIndex(a => a?.trait_type === trait_type);
  const entry = { trait_type, value };
  if (idx >= 0) next[idx] = entry;
  else next.push(entry);
  return next;
}

async function fetchIpfsJson(uri, { signal } = {}) {
  if (!uri) throw new Error('Missing IPFS URI');
  const errors = [];
  for (const base of IPFS_GATEWAYS) {
    const url = resolveIpfsUrl(uri, base);
    try {
      const res = await fetch(url, { mode: 'cors', signal, headers: { 'Accept': 'application/json' } });
      if (res.ok) {
        return await res.json();
      }
      errors.push(`${base} -> HTTP ${res.status}`);
      // Retry only on 429/5xx; otherwise break.
      if (res.status < 500 && res.status !== 429) break;
    } catch (e) {
      errors.push(`${base} -> ${e.message}`);
    }
  }
  throw new Error(`All IPFS gateways failed for ${uri}. Attempts: ${errors.join(' | ')}`);
}

function normalizeMetadataShape(metadata, tokenId) {
  const safe = metadata && typeof metadata === "object" ? metadata : {};
  const attrs = Array.isArray(safe.attributes) ? safe.attributes : [];
  return {
    ...safe,
    name: safe.name || `Carbon Credit Certificate #${tokenId}`,
    description: safe.description || "",
    attributes: attrs
  };
}

function pickLatestLog(logs) {
  if (!Array.isArray(logs) || logs.length === 0) return null;
  return [...logs].sort((a, b) => {
    const blockA = Number(a?.blockNumber ?? 0);
    const blockB = Number(b?.blockNumber ?? 0);
    if (blockA !== blockB) return blockA - blockB;
    const idxA = Number(a?.index ?? a?.logIndex ?? 0);
    const idxB = Number(b?.index ?? b?.logIndex ?? 0);
    return idxA - idxB;
  })[logs.length - 1];
}

async function resolveMintEvidenceByTokenId(nftContract, tokenId) {
  const result = {
    txHash: "",
    blockNumber: "",
    timestamp: "",
    source: ""
  };

  if (!nftContract || tokenId === null || tokenId === undefined || tokenId === "") {
    return result;
  }

  let tokenIdBig;
  try {
    tokenIdBig = BigInt(tokenId);
  } catch {
    return result;
  }

  const provider = nftContract?.runner?.provider;
  if (!provider) return result;

  const iface = nftContract.interface;
  const tokenTopic = ethers.zeroPadValue(ethers.toBeHex(tokenIdBig), 32);
  const zeroFromTopic = ethers.zeroPadValue(ethers.ZeroAddress, 32);

  const getTopicHash = (eventName) => {
    try {
      return iface.getEvent(eventName).topicHash;
    } catch {
      return null;
    }
  };

  const mintedV2Topic = getTopicHash("CarbonCreditMintedV2");
  const mintedTopic = getTopicHash("CarbonCreditMinted");
  const transferTopic = getTopicHash("Transfer");

  const fetchLogs = async (topic, topicsTail, fromBlock = 0, toBlock = "latest") => {
    if (!topic) return [];
    try {
      return await provider.getLogs({
        address: nftContract.target,
        topics: [topic, ...topicsTail],
        fromBlock,
        toBlock
      });
    } catch {
      return [];
    }
  };

  let mintedV2Logs = await fetchLogs(mintedV2Topic, [null, tokenTopic]);
  let mintedLogs = await fetchLogs(mintedTopic, [null, tokenTopic]);
  let transferMintLogs = await fetchLogs(transferTopic, [zeroFromTopic, null, tokenTopic]);

  let partC = null;
  try {
    partC = await nftContract.getCertificatePartC(tokenId);
  } catch {
    partC = null;
  }

  const mintedBlockFromCert = partC?.mintedBlock || partC?.[5];
  const mintedAtFromCert = partC?.mintedAt || partC?.[4];

  if (!mintedV2Logs.length && !mintedLogs.length && !transferMintLogs.length && mintedBlockFromCert != null) {
    const certBlock = Number(mintedBlockFromCert);
    if (Number.isFinite(certBlock) && certBlock > 0) {
      mintedV2Logs = await fetchLogs(mintedV2Topic, [null, tokenTopic], certBlock, certBlock);
      mintedLogs = await fetchLogs(mintedTopic, [null, tokenTopic], certBlock, certBlock);
      transferMintLogs = await fetchLogs(transferTopic, [zeroFromTopic, null, tokenTopic], certBlock, certBlock);
    }
  }

  const chosen = pickLatestLog(mintedV2Logs) || pickLatestLog(mintedLogs) || pickLatestLog(transferMintLogs);
  if (chosen?.transactionHash) {
    result.txHash = String(chosen.transactionHash);
    result.blockNumber = String(chosen.blockNumber ?? "");
    result.source = pickLatestLog(mintedV2Logs)
      ? "CarbonCreditMintedV2"
      : pickLatestLog(mintedLogs)
      ? "CarbonCreditMinted"
      : "Transfer(mint)";

    try {
      const block = await provider.getBlock(chosen.blockNumber);
      if (block?.timestamp != null) {
        result.timestamp = String(block.timestamp);
      }
    } catch {}
  }

  if (!result.blockNumber && mintedBlockFromCert != null) {
    result.blockNumber = String(mintedBlockFromCert);
  }
  if (!result.timestamp && mintedAtFromCert != null) {
    result.timestamp = String(mintedAtFromCert);
  }

  return result;
}

function applyMintEvidenceToMetadata(metadata, tokenId, mintEvidence) {
  const normalized = normalizeMetadataShape(metadata, tokenId);
  let attrs = Array.isArray(normalized.attributes) ? normalized.attributes : [];

  if (mintEvidence?.txHash) {
    attrs = upsertAttribute(attrs, "Mint Tx Hash", String(mintEvidence.txHash));
  }
  if (mintEvidence?.blockNumber) {
    attrs = upsertAttribute(attrs, "Mint Block Number", String(mintEvidence.blockNumber));
  }
  if (mintEvidence?.timestamp) {
    attrs = upsertAttribute(attrs, "Mint Timestamp", String(mintEvidence.timestamp));
  }

  return {
    ...normalized,
    attributes: attrs
  };
}

// ------------------------------------------------------------------------------


export default function CarbonDashboards({ themeMode }) {
  // --- V2 mint manual lot selection state ---
  const [certificatePrice, setCertificatePrice] = useState(null);
  const [numNftsToMint, setNumNftsToMint] = useState(1);
  const [userLots, setUserLots] = useState([]);
  const [selectedLots, setSelectedLots] = useState([]);
  const [maxMintableNfts, setMaxMintableNfts] = useState(0);
  const [carbon, setCarbon] = useState(null); // moved up from below
  const [carbonReadAddress, setCarbonReadAddress] = useState(CARBON_ADDRESS || "");
  const [account, setAccount] = useState("");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", themeMode);
  }, [themeMode]);

  const { enqueueSnackbar } = useSnackbar();
  const { isUserBlocked } = useBlockedUsers();

  // duplicate removed
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  //  const [account, setAccount] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [vaultStatus, setVaultStatus] = useState({ deposited: 0, verified: false, minting: false });
  const [depositAmount, setDepositAmount] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [tokenURI, setTokenURI] = useState("");
  const [nftPreview, setNftPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [retiredEvents, setRetiredEvents] = useState([]);
  // For admin: complete chain-wide history (not filtered to current user)
  const [historyAll, setHistoryAll] = useState([]);
  const [retiredEventsAll, setRetiredEventsAll] = useState([]);
  const [mintedMetadataList, setMintedMetadataList] = useState([]);

  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyAddress, setVerifyAddress] = useState("");
  const [verifyAction, setVerifyAction] = useState(true); // true = verify, false = unverify
  const [verifyLoading, setVerifyLoading] = useState(false);

  const [marketplacePaused, setMarketplacePaused] = useState(false);
  const [pauseMarketplaceDialogOpen, setPauseMarketplaceDialogOpen] = useState(false);
  const [updateCarbonPriceDialogOpen, setUpdateCarbonPriceDialogOpen] = useState(false);
  const [updateErgonPriceDialogOpen, setUpdateErgonPriceDialogOpen] = useState(false);
  const [carbonPriceInput, setCarbonPriceInput] = useState("");
  const [ergonPriceInput, setErgonPriceInput] = useState("");

  const [paused, setPaused] = useState(false);
  // duplicate hooks removed; only one useState remains per state
  const [connected, setConnected] = useState(false);
  const [vault, setVault] = useState(null);
  const [nft, setNft] = useState(null);
  const [marketplace, setMarketplace] = useState(null);
  // Minting state
  const [minting, setMinting] = useState(false);
  const [mintStatus, setMintStatus] = useState("");
  const [mintedTokenId, setMintedTokenId] = useState(null);
  const [mintedTokenURI, setMintedTokenURI] = useState("");
  const [mintedMetadata, setMintedMetadata] = useState(null);
  // Mint V2 (provenance burn) inputs
  const [useV2Mint] = useState(true);
  const [v2BurnPlanInput, setV2BurnPlanInput] = useState("");
  const [v2PlanDialogOpen, setV2PlanDialogOpen] = useState(false);
  const [v2PlanPreview, setV2PlanPreview] = useState(null);
  const [v2PlanApprovedSnapshot, setV2PlanApprovedSnapshot] = useState(null);

  // Fetch user lots from chain
  const fetchUserLots = useCallback(async (options = {}) => {
    const keepSelection = Boolean(options?.keepSelection);
    const forceWalletProvider = Boolean(options?.forceWalletProvider);

    if (!account) {
      setUserLots([]);
      setSelectedLots([]);
      return [];
    }

    let lotReader = carbon;
    let readSource = "wallet-provider";

    if (!forceWalletProvider && looksLikeAddress(carbonReadAddress) && LOT_READ_RPC_URL) {
      try {
        const readProvider = new ethers.JsonRpcProvider(LOT_READ_RPC_URL);
        lotReader = new Contract(carbonReadAddress, CARBON_ABI, readProvider);
        readSource = `rpc:${LOT_READ_RPC_URL}`;
      } catch (e) {
        console.warn("[fetchUserLots] read provider init failed, using wallet provider:", e?.message || e);
      }
    }

    if (!lotReader) {
      setUserLots([]);
      setSelectedLots([]);
      return [];
    }

    try {
      const len = Number(await lotReader.lotIdsLength(account));
      const cursor = Number(await lotReader.lotCursor(account));

      const lots = [];
      for (let i = cursor; i < len; i++) {
        const id = await lotReader.lotIdAt(account, i);
        const bal = await lotReader.lotBalance(account, id);
        const b = BigInt(bal);

        if (b === 0n) continue;
        lots.push({
          messageId: String(id),
          balanceWei: b.toString(),
          balanceToken: ethers.formatUnits(b, 18),
          idx: i,
        });
      }

      setUserLots(lots);
      console.log(`[fetchUserLots] Loaded ${lots.length} non-zero lots from ${readSource}`);
      setSelectedLots((prev) => {
        const all = lots.map((l) => l.messageId);
        if (!keepSelection) return all;

        const valid = new Set(all);
        const kept = (Array.isArray(prev) ? prev : []).filter((id) => valid.has(id));
        return kept.length > 0 ? kept : all;
      });

      return lots;
    } catch (e) {
      console.error("[fetchUserLots] failed", e);
      setUserLots([]);
      setSelectedLots([]);
      return [];
    }
  }, [carbon, account, carbonReadAddress]);

  useEffect(() => {
    if (v2PlanDialogOpen && useV2Mint) fetchUserLots({ keepSelection: true });
  }, [v2PlanDialogOpen, useV2Mint, fetchUserLots]);

  // Keep max mintable count in sync with selected lots and certificate price.
  useEffect(() => {
    try {
      const certWei = BigInt(certificatePrice || 0);
      if (certWei <= 0n) {
        setMaxMintableNfts(0);
        return;
      }

      const totalSelectedWei = userLots
        .filter((l) => selectedLots.includes(l.messageId))
        .reduce((sum, lot) => sum + BigInt(lot.balanceWei || 0), 0n);

      const max = Number(totalSelectedWei / certWei);
      setMaxMintableNfts(max);

      setNumNftsToMint((prev) => {
        if (max <= 0) return 1;
        if (prev < 1) return 1;
        if (prev > max) return max;
        return prev;
      });
    } catch {
      setMaxMintableNfts(0);
    }
  }, [certificatePrice, userLots, selectedLots]);

  const parseBurnPlanInput = useCallback((raw) => {
    const trimmed = String(raw || "").trim();
    if (!trimmed) throw new Error("V2 burn plan is empty");

    const tryParseAmount = (v) => {
      if (typeof v === 'bigint') return v;
      if (typeof v === 'number') {
        if (!Number.isFinite(v)) throw new Error("Invalid amount");
        if (String(v).includes('.')) return parseUnits(String(v), 18);
        return BigInt(Math.trunc(v));
      }
      const s = String(v).trim();
      if (!s) throw new Error("Invalid amount");
      if (s.includes('.')) return parseUnits(s, 18);
      return BigInt(s);
    };

    const normalizeBytes32 = (id) => {
      const s = String(id).trim();
      if (!/^0x[0-9a-fA-F]{64}$/.test(s)) {
        throw new Error(`Invalid messageId (expected bytes32 hex): ${s}`);
      }
      return s;
    };

    // JSON formats:
    // 1) { messageIds: [...], amounts: [...] }
    // 2) [ { messageId, amount }, ... ]
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      const parsed = JSON.parse(trimmed);
      let messageIds = [];
      let amounts = [];

      if (Array.isArray(parsed)) {
        for (const entry of parsed) {
          if (!entry) continue;
          messageIds.push(normalizeBytes32(entry.messageId ?? entry.messageIdBurned ?? entry.id));
          amounts.push(tryParseAmount(entry.amount ?? entry.amountBurned ?? entry.amt));
        }
      } else {
        messageIds = (parsed.messageIds || []).map(normalizeBytes32);
        amounts = (parsed.amounts || []).map(tryParseAmount);
      }

      if (messageIds.length === 0 || amounts.length === 0) throw new Error("Burn plan JSON missing messageIds/amounts");
      if (messageIds.length !== amounts.length) throw new Error("Burn plan length mismatch");
      return { messageIds, amounts };
    }

    // Line format: messageId:amount
    const lines = trimmed.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const messageIds = [];
    const amounts = [];
    for (const line of lines) {
      const parts = line.split(':');
      if (parts.length !== 2) throw new Error(`Invalid burn plan line (expected messageId:amount): ${line}`);
      messageIds.push(normalizeBytes32(parts[0]));
      amounts.push(tryParseAmount(parts[1]));
    }
    if (messageIds.length === 0) throw new Error("Burn plan empty");
    return { messageIds, amounts };
  }, []);

  const applyV2BurnPlanLocally = useCallback((messageIds, amounts) => {
    if (!Array.isArray(messageIds) || !Array.isArray(amounts) || messageIds.length !== amounts.length) return;

    const burnById = new Map();
    for (let i = 0; i < messageIds.length; i++) {
      const key = String(messageIds[i]).toLowerCase();
      const amt = BigInt(amounts[i] || 0n);
      burnById.set(key, (burnById.get(key) || 0n) + amt);
    }

    setUserLots((prev) => {
      const next = [];
      for (const lot of (Array.isArray(prev) ? prev : [])) {
        const key = String(lot.messageId || "").toLowerCase();
        const current = BigInt(lot.balanceWei || 0n);
        const burn = burnById.get(key) || 0n;
        const updated = current > burn ? current - burn : 0n;
        if (updated > 0n) {
          next.push({
            ...lot,
            balanceWei: updated.toString(),
            balanceToken: ethers.formatUnits(updated, 18)
          });
        }
      }
      return next;
    });
  }, []);


  // Manual V2 burn plan preview: build plan from selected lots and numNftsToMint
  const openV2BurnPlanPreview = useCallback(async () => {
    if (!useV2Mint) {
      enqueueSnackbar("Enable V2 mint first", { variant: "info" });
      return;
    }
    if (!nft || !certificatePrice) {
      enqueueSnackbar("NFT contract not loaded", { variant: "error" });
      return;
    }
    let errorMsg = null;
    let preview = null;
    try {
      // Always refresh lots from chain before computing preview, otherwise stale balances
      // may remain in UI until a full page reload.
      const latestLots = await fetchUserLots({ keepSelection: true });
      const lotsSource = latestLots.length > 0 ? latestLots : userLots;
      const validIds = new Set(lotsSource.map((l) => l.messageId));
      let selectedIds = selectedLots.filter((id) => validIds.has(id));
      if (selectedIds.length === 0) {
        selectedIds = lotsSource.map((l) => l.messageId);
      }

      const certWei = BigInt(certificatePrice);
      const totalNeeded = certWei * BigInt(numNftsToMint);
      // Only use selected lots
      const lots = lotsSource.filter((l) => selectedIds.includes(l.messageId));
      let remaining = totalNeeded;
      const messageIds = [];
      const amounts = [];
      for (const lot of lots) {
        if (remaining <= 0n) break;
        const take = BigInt(lot.balanceWei) < remaining ? BigInt(lot.balanceWei) : remaining;
        messageIds.push(lot.messageId);
        amounts.push(take);
        remaining -= take;
      }
      // Build burn plan input for preview
      const coder = ethers.AbiCoder.defaultAbiCoder();
      const encoded = coder.encode(["bytes32[]", "uint256[]"], [messageIds, amounts]);
      const provenanceRootPreview = ethers.keccak256(encoded);
      const rows = messageIds.map((id, i) => ({
        messageId: id,
        amountWei: amounts[i],
        amountToken: ethers.formatUnits(amounts[i], 18)
      }));
      preview = {
        requiredBurnWei: totalNeeded,
        requiredBurnToken: ethers.formatUnits(totalNeeded, 18),
        plannedBurnWei: amounts.reduce((a, b) => a + b, 0n),
        plannedBurnToken: ethers.formatUnits(amounts.reduce((a, b) => a + b, 0n), 18),
        matches: remaining <= 0n,
        provenanceRootPreview,
        rows,
        numNfts: numNftsToMint
      };
      // Set burn plan input for mintNFT
      setV2BurnPlanInput(JSON.stringify({ messageIds, amounts: amounts.map(a => a.toString()) }, null, 2));
      if (remaining > 0n) {
        errorMsg = "Not enough balance in selected lots for the requested number of NFTs.";
      }
    } catch (err) {
      errorMsg = err.message || String(err);
    }
    setV2PlanPreview(preview);
    setV2PlanDialogOpen(true);
    // errorMsg can be displayed in the dialog
    setTimeout(() => setV2PlanPreview(prev => prev ? { ...prev, errorMsg } : { errorMsg }), 0);
  }, [useV2Mint, nft, certificatePrice, userLots, selectedLots, numNftsToMint, enqueueSnackbar, fetchUserLots]);

  const autoFillV2BurnPlanFromChain = useCallback(async () => {
    if (!useV2Mint) return;
    if (!carbon || !nft) {
      enqueueSnackbar("Contracts not loaded (CARBON/NFT)", { variant: "error" });
      return;
    }
    if (!account) {
      enqueueSnackbar("Wallet not connected", { variant: "error" });
      return;
    }

    try {
      setMintStatus("Building V2 burn plan from on-chain lots...");

      const requiredBurn = await nft.calculateCarbonTokenAmount();
      let remaining = BigInt(requiredBurn);

      const cursor = Number(await carbon.lotCursor(account));
      const len = Number(await carbon.lotIdsLength(account));

      const messageIds = [];
      const amounts = [];

      for (let i = cursor; i < len && remaining > 0n; i++) {
        const id = await carbon.lotIdAt(account, i);
        const bal = BigInt(await carbon.lotBalance(account, id));
        if (bal === 0n) continue;
        const take = bal < remaining ? bal : remaining;
        messageIds.push(String(id));
        amounts.push(take);
        remaining -= take;
      }

      if (remaining !== 0n) {
        throw new Error(`Not enough lot balance to satisfy required burn. Missing ${remaining.toString()} wei.`);
      }

      // Store as JSON to avoid line formatting issues
      const burnPlanJson = JSON.stringify(
        {
          messageIds,
          amounts: amounts.map(a => a.toString())
        },
        null,
        2
      );
      setV2BurnPlanInput(burnPlanJson);
      enqueueSnackbar("V2 burn plan auto-filled from wallet lots", { variant: "success" });
      // Open preview right away so user sees exactly what's about to be burned.
      setTimeout(() => {
        try {
          openV2BurnPlanPreview();
        } catch {}
      }, 0);
    } catch (err) {
      console.error("[autoFillV2BurnPlanFromChain] error", err);
      enqueueSnackbar(`Failed to build burn plan: ${err.message || err}`, { variant: "error" });
    }
  }, [useV2Mint, carbon, nft, account, enqueueSnackbar, openV2BurnPlanPreview]);

  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  //const [certificatePrice, setCertificatePrice] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [priceInput, setPriceInput] = useState("");
  const [listingTokenId, setListingTokenId] = useState(null);

  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [metadataDialogContent, setMetadataDialogContent] = useState(null);
  const [metadataDevicesLoading, setMetadataDevicesLoading] = useState(false);
  const [metadataDevicesError, setMetadataDevicesError] = useState("");
  const [metadataDevicesReport, setMetadataDevicesReport] = useState(null);

// ###################### New Added ######################################
  const [listingDialogOpen, setListingDialogOpen] = useState(false);
  const [listingType, setListingType] = useState("fixed");
  const [durationInput, setDurationInput] = useState("60");

  const [nftPage, setNftPage] = useState(0);
  const [nftRowsPerPage, setNftRowsPerPage] = useState(3);
// ###################### New Ended ######################################




  // --- Function to cancel listing for a token ---
  async function onCancelListing(tokenId) {
    // Check if user is blocked
    if (isUserBlocked(account)) {
      enqueueSnackbar("You are blocked from performing this action. Please contact support if you believe this is an error.", { variant: "error" });
      return;
    }

    if (!marketplace) return;
    try {
      const tx = await marketplace.cancelListing(tokenId);
      await tx.wait();
      // Optionally refresh NFT list or update state here
      setMintedMetadataList(prev =>
        prev.map(nft =>
          nft.tokenId === tokenId
            ? { ...nft, isListed: false, price: null }
            : nft
        )
      );
      await refreshMintedMetadataList();      

    } catch (err) {
      alert("Failed to cancel listing: " + (err.reason || err.message));
    }
  }

// ###################### New Added ######################################

  function handleListClick(tokenId) {
    setListingTokenId(tokenId);
    setPriceInput("");
    setDurationInput("60");
    setListingType("fixed");
    setListingDialogOpen(true);
  }

// Find and replace the listNFT function with this corrected version:

// Replace the listNFT function (around lines 155-190) with this corrected version:

async function listNFT(tokenId, priceEth) {
  // Check if user is blocked
  if (isUserBlocked(account)) {
    enqueueSnackbar("You are blocked from performing this action. Please contact support if you believe this is an error.", { variant: "error" });
    return;
  }

  try {
    if (!window.ethereum) {
      enqueueSnackbar("Web3 wallet not found. Install or enable MetaMask!", { variant: "error" });
      return;
    }
    const provider = new BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const nft = new Contract(NFT_ADDRESS, NFT_ABI, signer);
    const marketplace = new Contract(MARKETPLACE_ADDRESS, MARKETPLACE_ABI, signer);

    console.log(`Listing NFT #${tokenId} for ${priceEth} ETH`);

    // First approve the marketplace to transfer this token
    console.log("Approving marketplace...");
    const approveTx = await nft.approve(MARKETPLACE_ADDRESS, tokenId);
    await approveTx.wait();
    console.log("Approved!");

    const priceWei = parseEther(priceEth);
    console.log("Price in Wei:", priceWei.toString());

    // Use the correct method name: listToken (not listNFT)
    console.log("Listing token...");
    const listTx = await marketplace.listToken(tokenId, priceWei);
    await listTx.wait();
    console.log("Listed!");

    enqueueSnackbar(`NFT #${tokenId} listed for ${priceEth} ETH!`, { variant: "success" });
    
    // Refresh the NFTs to show updated status
    await refreshMintedMetadataList();

  } catch (err) {
    console.error("Listing error:", err);
    enqueueSnackbar(`Failed to list NFT: ${err.reason || err.message}`, { variant: "error" });
  }
}

async function handleListingDialogConfirm() {
  // Check if user is blocked
  if (isUserBlocked(account)) {
    enqueueSnackbar("You are blocked from performing this action. Please contact support if you believe this is an error.", { variant: "error" });
    setListingDialogOpen(false);
    return;
  }

  if (!marketplace || !listingTokenId || !priceInput) return;
  setListingDialogOpen(false);
  try {
    if (!window.ethereum) {
      enqueueSnackbar("Web3 wallet not found. Install or enable MetaMask!", { variant: "error" });
      return;
    }
    const provider = new BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    
    // *** FIX: Create contracts with signer, not using existing provider-only contracts ***
    const nftWithSigner = new Contract(NFT_ADDRESS, NFT_ABI, signer);
    const marketplaceWithSigner = new Contract(MARKETPLACE_ADDRESS, MARKETPLACE_ABI, signer);

    console.log("=== DEBUGGING AUCTION START ===");
    console.log("NFT_ADDRESS:", NFT_ADDRESS);
    console.log("MARKETPLACE_ADDRESS:", MARKETPLACE_ADDRESS);
    console.log("Token ID:", listingTokenId);
    console.log("Listing Type:", listingType);
    console.log("Price Input:", priceInput);
    console.log("Duration Input:", durationInput);

    const actualMarketplaceAddress = await marketplaceWithSigner.getAddress();
    const signerAddress = await signer.getAddress();

    // Enhanced ownership and approval debugging
    const currentOwner = await nftWithSigner.ownerOf(listingTokenId);
    const approved = await nftWithSigner.getApproved(listingTokenId);
    const isApprovedForAll = await nftWithSigner.isApprovedForAll(signerAddress, actualMarketplaceAddress);
    
    console.log("Current owner:", currentOwner);
    console.log("Signer address:", signerAddress);
    console.log("Owner match:", currentOwner.toLowerCase() === signerAddress.toLowerCase());
    console.log("Approved to:", approved);
    console.log("Marketplace address:", actualMarketplaceAddress);
    console.log("Approval match:", approved.toLowerCase() === actualMarketplaceAddress.toLowerCase());
    console.log("Is approved for all:", isApprovedForAll);

    // Check if contracts are properly connected
    console.log("=== CONTRACT CONNECTION CHECK ===");
    console.log("NFT contract target:", await nftWithSigner.getAddress());
    console.log("Marketplace contract target:", await marketplaceWithSigner.getAddress());
    console.log("Signer connected to NFT:", nftWithSigner.signer === signer);
    console.log("Signer connected to Marketplace:", marketplaceWithSigner.signer === signer);

    // Ensure approval (even if already approved, re-approve to be safe)
    console.log("=== RE-APPROVING FOR SAFETY ===");
    if (approved.toLowerCase() !== actualMarketplaceAddress.toLowerCase() && !isApprovedForAll) {
      console.log("Approving marketplace...");
      const approveTx = await nftWithSigner.approve(actualMarketplaceAddress, listingTokenId);
      await approveTx.wait();
      
      const newApproved = await nftWithSigner.getApproved(listingTokenId);
      console.log("New approval:", newApproved);
      console.log("New approval match:", newApproved.toLowerCase() === actualMarketplaceAddress.toLowerCase());
    } else {
      console.log("Already approved, skipping approval");
    }

    // Check retirement and auction status
    let auctionActive = false;
    try {
      const auction = await marketplaceWithSigner.auctions(listingTokenId);
      auctionActive = auction.active;
      console.log("Auction active:", auctionActive);
      console.log("Auction details:", auction);
    } catch (e) {
      console.log("No auction found or error:", e.message);
    }

    let retired = false;
    try {
      const retirementDetails = await nftWithSigner.getRetirementDetails(listingTokenId);
      retired = retirementDetails.isRetired;
      console.log("Retired:", retired);
      console.log("Retirement details:", retirementDetails);
    } catch (e) {
      console.log("Could not check retirement status:", e.message);
    }

    // Validate conditions
    if (currentOwner.toLowerCase() !== signerAddress.toLowerCase()) {
      throw new Error("You are not the owner of this NFT.");
    }
    if (auctionActive) {
      throw new Error("This NFT is already in an active auction.");
    }
    if (retired) {
      throw new Error("This NFT is retired and cannot be listed.");
    }

    // Prepare transaction parameters
    const priceWei = ethers.parseEther(priceInput);
    console.log("=== TRANSACTION PARAMETERS ===");
    console.log("Price in Wei:", priceWei.toString());

    if (listingType === "fixed") {
      console.log("=== FIXED PRICE LISTING ===");
      console.log("Calling listNFT with params:", {
        tokenId: listingTokenId,
        price: priceWei.toString()
      });
      
      const listTx = await marketplaceWithSigner.listToken(listingTokenId, priceWei);
      await listTx.wait();
      console.log("NFT listed successfully!");
      
    } else {
      console.log("=== AUCTION LISTING ===");
      const durationSeconds = parseInt(durationInput) * 60;
      if (durationSeconds < 3600) { // 1 hour = 3600 seconds
        throw new Error("Auction duration must be at least 60 minutes (1 hour)");
      }
      
      console.log("=== AUCTION DURATION DEBUG ===");
      console.log("Duration input (minutes):", durationInput);
      console.log("Duration in seconds:", durationSeconds);
      console.log("Current timestamp:", Math.floor(Date.now() / 1000));
      console.log("Expected end time:", Math.floor(Date.now() / 1000) + durationSeconds);
      
      console.log("Calling startAuction with params:", {
        tokenId: listingTokenId,
        startingPrice: priceWei.toString(),
        duration: durationSeconds,
        expectedEndTime: Math.floor(Date.now() / 1000) + durationSeconds
      });
      
      const auctionTx = await marketplaceWithSigner.startAuction(listingTokenId, priceWei, durationSeconds);
      await auctionTx.wait();
      console.log("Auction started successfully!");
    }

    enqueueSnackbar(`NFT #${listingTokenId} listed as ${listingType}!`, { variant: "success" });
    setListingTokenId(null);
    setPriceInput("");
    setDurationInput("60");
    await refreshMintedMetadataList();

  } catch (err) {
    console.error("=== LISTING FAILED ===");
    console.error("Error:", err);
    console.error("Error message:", err.message);
    console.error("Error reason:", err.reason);
    console.error("Error code:", err.code);
    console.error("Error data:", err.data);
    alert("Failed to list NFT: " + (err.reason || err.message));
  }
}




// ###################### New Ended ######################################

  async function refreshMintedMetadataList(targetAccount = account) {
    console.log("Refreshing minted metadata list...");
    // Always use a provider for read-only operations
    if (!window.ethereum) {
      enqueueSnackbar("Web3 wallet not found. Install or enable MetaMask!", { variant: "error" });
      return;
    }
    const provider = new ethers.BrowserProvider(window.ethereum);
    const nftInstance = new Contract(NFT_ADDRESS, NFT_ABI, provider);
    const marketplaceInstance = new Contract(MARKETPLACE_ADDRESS, MARKETPLACE_ABI, provider);

    const activeAccount = targetAccount || account;
    if (!nftInstance || !marketplaceInstance || !activeAccount) return;

    // Get all tokenIds minted by user
    const mintEvents = await nftInstance.queryFilter("CarbonCreditMinted", 0, "latest");
    if (mintEvents.length === 0) {
      setMintedTokenId(1);
      return;
    }
    const userMints = mintEvents.filter(
      e => String(e.args.to).toLowerCase() === String(activeAccount).toLowerCase()
    );
    const mintedTokenIds = userMints.map(e => e.args.tokenId.toString());

    // Get all NFTs currently owned by user
    let ownedTokenIds = [];
    let fallbackUsed = false;
    try {
      const balance = await nftInstance.balanceOf(activeAccount);
      for (let i = 0; i < balance; i++) {
        try {
          const tokenId = await nftInstance.tokenOfOwnerByIndex(activeAccount, i);
          ownedTokenIds.push(tokenId.toString());
        } catch (err) {
          fallbackUsed = true;
          break;
        }
      }
    } catch (err) {
      fallbackUsed = true;
    }
    if (fallbackUsed) {
      const maxTokenId = mintEvents.length > 0
        ? Math.max(...mintEvents.map(e => Number(e.args.tokenId)))
        : 1000;
      ownedTokenIds = await getOwnedTokenIds(nftInstance, activeAccount, maxTokenId);
    }

    // Deduplicate ownedTokenIds and mintedTokenIds
    const ownedSet = new Set(ownedTokenIds);
    const mintedSet = new Set(mintedTokenIds);
    const allTokenIds = Array.from(new Set([...ownedTokenIds, ...mintedTokenIds]));

    // Debug log
    console.log("[refreshMintedMetadataList] Minted token IDs:", mintedTokenIds);
    console.log("[refreshMintedMetadataList] Owned token IDs:", ownedTokenIds);
    console.log("[refreshMintedMetadataList] All token IDs:", allTokenIds);

    const metadataList = await Promise.all(allTokenIds.map(async (tokenId) => {
      try {
        const uri = await nftInstance.tokenURI(tokenId);
        let metadata = null;
        if (uri) {
          try {
            metadata = await fetchIpfsJson(uri);
          } catch (e) {
            console.warn(`[refreshMintedMetadataList] tokenId ${tokenId}: failed to fetch metadata from uri=${uri}`, e);
          }
        }

        if (!uri || !metadata) {
          // Legacy / broken mints (tokenURI empty). Keep NFT visible with a placeholder.
          metadata = {
            name: `Carbon Credit Certificate #${tokenId}`,
            description: "Legacy NFT: tokenURI is missing or metadata could not be fetched.",
            image: null,
            attributes: [
              { trait_type: "ID", value: String(tokenId) },
              { trait_type: "Metadata Status", value: "Missing IPFS URI" }
            ]
          };
        }

        // Fetch retire status from contract
        let retired = false;
        let retiredBy = null;
        let retiredTimestamp = null;

        try {
          const retirementDetails = await nftInstance.getRetirementDetails(tokenId);
          retired = retirementDetails.isRetired;
          retiredBy = retirementDetails.verifier;
          retiredTimestamp = retirementDetails.timestamp ? Number(retirementDetails.timestamp) : null;
        } catch (err) {
          console.log("Could not fetch retirement details for token", tokenId);
        }

        // Fetch listing status from contract
        let isListed = false;
        let isAuction = false;
        let price = null;

        try {
          const listing = await marketplaceInstance.listings(tokenId);
          if (listing.active) {
            isListed = true;
            price = listing.price?.toString?.() ?? null;
          }
        } catch {}

        try {
          const auction = await marketplaceInstance.auctions(tokenId);
          if (auction.active) {
            isAuction = true;
            price = auction.highestBid?.toString() || auction.startingPrice?.toString();
          }
        } catch {}

        const isOwned = ownedSet.has(tokenId.toString());
        const isMinted = mintedSet.has(tokenId.toString());
        const isPurchased = isOwned && !isMinted;

        // Debug log
        console.log(`[refreshMintedMetadataList] tokenId ${tokenId}: isOwned=${isOwned}, isMinted=${isMinted}, isPurchased=${isPurchased}`);

        return { tokenId, metadata, uri, isMinted, isPurchased, isListed, isAuction, isOwned, price, retired, retiredBy, retiredTimestamp };
      } catch (err) {
        console.log(`[refreshMintedMetadataList] Error loading tokenId ${tokenId}:`, err);
        return null;
      }
    }));

    const normalizedList = metadataList
      .filter(Boolean)
      .sort((a, b) => Number(b.tokenId) - Number(a.tokenId));

    setMintedMetadataList(normalizedList);
  }

  // --- Function to confirm listing (call this from your dialog's "List" button) ---
  async function handleDialogConfirm() {
    if (!marketplace || !listingTokenId || !priceInput) return;
    setDialogOpen(false);
    try {
      if (!window.ethereum) {
        enqueueSnackbar("Web3 wallet not found. Install or enable MetaMask!", { variant: "error" });
        return;
      }
      const provider = new BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const nft = new Contract(NFT_ADDRESS, NFT_ABI, signer);
      const marketplaceWithSigner = new Contract(MARKETPLACE_ADDRESS, MARKETPLACE_ABI, signer);

      // Approve Marketplace to transfer this token
      const approveTx = await nft.approve(MARKETPLACE_ADDRESS, listingTokenId);
      await approveTx.wait();

      const priceWei = ethers.parseEther(priceInput);

      // List token on Marketplace
      const listTx = await marketplaceWithSigner.listToken(listingTokenId, priceWei);
      await listTx.wait();

      setMintedMetadataList(prev =>
        prev.map(nft =>
          nft.tokenId === listingTokenId
            ? { ...nft, isListed: true, price: priceWei.toString() }
            : nft
        )
      );
      await refreshMintedMetadataList();
      setListingTokenId(null);
      setPriceInput("");
    } catch (err) {
      alert("Failed to list NFT: " + (err.reason || err.message));
    }
  }

  const handleKycSubmissionComplete = (result) => {
    setKycDialogOpen(false);
    enqueueSnackbar("KYC application submitted successfully! Our admin will review your application.", { variant: "success" });
  };

  // Helper to refresh vault status
  /*
  const refreshVaultStatus = async () => {
    if (!vault || !account) return;
    const [deposited, verified, minting] = await vault.getVaultStatus(account);
    setVaultStatus({ deposited: deposited.toString(), verified, minting });
  };
  */

  // Helper to scan all token IDs for ownership (for non-ERC721Enumerable contracts)
  async function getOwnedTokenIds(nftInstance, user, maxTokenId = 1000) {
    const tokenIds = [];
    for (let tokenId = 1; tokenId <= maxTokenId; tokenId++) {
      try {
        const owner = await nftInstance.ownerOf(tokenId);
        if (owner.toLowerCase() === user.toLowerCase()) {
          tokenIds.push(tokenId.toString());
        }
      } catch {
        // token does not exist or error, skip
      }
    }
    return tokenIds;
  }  

  const refreshVaultStatus = useCallback(async () => {
    if (!vault || !account) return;
    const [deposited, verified, minting] = await vault.getVaultStatus(account);
    setVaultStatus({ deposited: deposited.toString(), verified, minting });
  }, [vault, account]);

  useEffect(() => {
    try {
      const depositedWei = BigInt(vaultStatus?.deposited || 0);
      if (depositedWei > 0n) {
        setRefundAmount(ethers.formatUnits(depositedWei, 18));
      } else {
        setRefundAmount("");
      }
    } catch {
      setRefundAmount("");
    }
  }, [vaultStatus?.deposited]);

  // Add this function to handle auction finalization:
  const handleAuctionFinalized = useCallback((tokenId) => {
    console.log(`Auction finalized for token ${tokenId}, refreshing marketplace...`);
    
    // Remove the finalized auction from the displayed NFTs
    setMintedMetadataList(prev => prev.filter(nft => 
      !(nft.tokenId === tokenId && nft.isAuction)
    ));
    
    // Refresh the entire list to get updated data
    refreshMintedMetadataList();
  }, []);  
  
  // Connect wallet and set up contracts
  async function connectWallet() {
    try {
      // Check if MetaMask is installed
      if (!window.ethereum) {
        setMintStatus("Please install MetaMask!");
        enqueueSnackbar("Please install MetaMask to use this application!", { variant: "error" });
        return;
      }

      // Check if MetaMask is ready
      if (!window.ethereum.isMetaMask) {
        enqueueSnackbar("Please use MetaMask as your wallet provider", { variant: "warning" });
      }

      setMintStatus("Connecting to wallet...");
      enqueueSnackbar("Connecting to MetaMask...", { variant: "info" });

      // Request account access with timeout
      const accounts = await Promise.race([
        window.ethereum.request({ method: "eth_requestAccounts" }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error("MetaMask connection timeout")), 10000)
        )
      ]);

      if (!accounts || accounts.length === 0) {
        throw new Error("No accounts found. Please connect your wallet.");
      }

      if (!window.ethereum) {
        enqueueSnackbar("Web3 wallet not found. Install or enable MetaMask!", { variant: "error" });
        return;
      }
      const p = new ethers.BrowserProvider(window.ethereum);
      const s = await p.getSigner();
      const user = await s.getAddress();
      
      setProvider(p);
      setSigner(s);
      setAccount(user);
      setConnected(true);
      
      enqueueSnackbar("Wallet connected successfully!", { variant: "success" });
      setMintStatus("Loading contracts...");
      
      await loadContracts(p, s, user);
      
    } catch (error) {
      console.error("Wallet connection error:", error);
      setMintStatus("Failed to connect wallet");
      
      if (error.code === 4001) {
        enqueueSnackbar("Connection rejected. Please approve the connection in MetaMask.", { variant: "warning" });
      } else if (error.message.includes("timeout")) {
        enqueueSnackbar("Connection timeout. Please try again and make sure MetaMask is unlocked.", { variant: "error" });
      } else {
        enqueueSnackbar(`Failed to connect wallet: ${error.message}`, { variant: "error" });
      }
      
      setConnected(false);
    }
  }

  // Load contracts and user data
  async function loadContracts(p, s, user, options = {}) {
    try {
      // Print debug info before loading contracts
      const net = await p.getNetwork();
      const chainId = net?.chainId;
      const networkId = process.env.REACT_APP_NETWORK_ID;
      console.log('[DEBUG] Attempting to load contracts with:');
      console.log('  chainId from provider:', chainId);
      console.log('  REACT_APP_NETWORK_ID:', networkId);
      console.log('  VAULT_ADDRESS:', VAULT_ADDRESS);
      console.log('  NFT_ADDRESS:', NFT_ADDRESS);
      console.log('  CARBON_ADDRESS:', CARBON_ADDRESS);
      console.log('  MARKETPLACE_ADDRESS:', MARKETPLACE_ADDRESS);
      console.log('  RPC_URL:', process.env.REACT_APP_RPC_URL);

      // Runtime resolution to avoid cross-machine networkId mismatches
      const [rv, rn, rc, rm] = await Promise.all([
        resolveArtifactAddress(p, vaultArtifact, 'PlatformVault'),
        resolveArtifactAddress(p, nftArtifact, 'CarbonCreditNFT'),
        resolveArtifactAddress(p, carbonArtifact, 'ETHCarbon'),
        resolveArtifactAddress(p, marketplaceArtifact, 'CarbonCreditMarketplace'),
      ]);

      const vaultAddr = looksLikeAddress(rv) ? rv : VAULT_ADDRESS;
      const nftAddr = looksLikeAddress(rn) ? rn : NFT_ADDRESS;
      const carbonAddr = looksLikeAddress(rc) ? rc : CARBON_ADDRESS;
      const marketAddr = looksLikeAddress(rm) ? rm : MARKETPLACE_ADDRESS;

      // Validate bytecode presence
      const [codeV, codeN, codeC, codeM] = await Promise.all([
        p.getCode(vaultAddr), p.getCode(nftAddr), p.getCode(carbonAddr), p.getCode(marketAddr)
      ]);
      console.log('[addr-final]', { vaultAddr, nftAddr, carbonAddr, marketAddr });
      if (codeV === '0x') throw new Error(`Vault address has no code: ${vaultAddr}`);
      if (codeN === '0x') throw new Error(`NFT address has no code: ${nftAddr}`);
      if (codeC === '0x') throw new Error(`Carbon address has no code: ${carbonAddr}`);
      if (codeM === '0x') throw new Error(`Marketplace address has no code: ${marketAddr}`);

      const vaultInstance = new Contract(vaultAddr, VAULT_ABI, s);
      const nftInstance = new Contract(nftAddr, NFT_ABI, s);
      const carbonInstance = new Contract(carbonAddr, CARBON_ABI, s);
      const marketplaceInstance = new Contract(marketAddr, MARKETPLACE_ABI, s);

      console.log("Contract instances created");
      
      setVault(vaultInstance);
      setNft(nftInstance);
      setCarbon(carbonInstance);
      setCarbonReadAddress(carbonAddr);
      setMarketplace(marketplaceInstance);    

      // Load contract data with timeout
      console.log("Loading contract data...");
      setMintStatus("Loading vault status...");
      
      let isPaused = false;
      if (supportsPausedMethod(vaultInstance)) {
        try {
          isPaused = await Promise.race([
            vaultInstance.paused(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error("Timeout loading pause status")), 5000)
            )
          ]);
        } catch (e) {
          console.warn('paused() call failed on initial load; continuing with paused=false.', e?.message || e);
        }
      }
      setPaused(isPaused);

      const [deposited, verified, minting] = await Promise.race([
        vaultInstance.getVaultStatus(user),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error("Timeout loading vault status")), 5000)
        )
      ]);
    setVaultStatus({ deposited: deposited.toString(), verified, minting });

    // Check admin status with retry logic for reliability
    await checkAdminStatus(vaultInstance, user);

  const mintEvents = await nftInstance.queryFilter("CarbonCreditMinted", 0, "latest");
  const retireEvents = await nftInstance.queryFilter("CarbonCreditRetired", 0, "latest");
    const userMints = mintEvents.filter(e => e.args.to === user);

    const mintedTokenIds = userMints.map(e => e.args.tokenId.toString());

    // Fetch timestamps for mint events
    const mintHistoryWithTimestamps = await Promise.all(
      userMints.map(async (event) => {
        let timestamp = null;
        try {
          if (!window.ethereum) {
            enqueueSnackbar("Web3 wallet not found. Install or enable MetaMask!", { variant: "error" });
            return;
          }
          const provider = new ethers.BrowserProvider(window.ethereum);
          const block = await provider.getBlock(event.blockNumber);
          if (block && block.timestamp) {
            timestamp = block.timestamp;
          }
        } catch (err) {
          console.log("Could not fetch timestamp for mint event:", err);
        }
        return { ...event, timestamp };
      })
    );

    setHistory(mintHistoryWithTimestamps);

    // Also prepare full (all-users) mint history with timestamps for Admin views
    const allMintHistoryWithTimestamps = await Promise.all(
      mintEvents.map(async (event) => {
        let timestamp = null;
        try {
          if (!window.ethereum) {
            enqueueSnackbar("Web3 wallet not found. Install or enable MetaMask!", { variant: "error" });
            return;
          }
          const provider = new ethers.BrowserProvider(window.ethereum);
          const block = await provider.getBlock(event.blockNumber);
          if (block && block.timestamp) {
            timestamp = block.timestamp;
          }
        } catch {}
        return { ...event, timestamp };
      })
    );
    setHistoryAll(allMintHistoryWithTimestamps);
    
    // Show retirement events for NFTs that the user has minted or owned, regardless of who retired them
    // First get all tokenIds user has interacted with (minted or owned)
    let ownedTokenIds = [];
    try {
      const balance = await nftInstance.balanceOf(user);
      for (let i = 0; i < balance; i++) {
        try {
          const tokenId = await nftInstance.tokenOfOwnerByIndex(user, i);
          ownedTokenIds.push(tokenId.toString());
        } catch {
          // fallback to scanning if enumeration fails
          const maxTokenId = mintEvents.length > 0
            ? Math.max(...mintEvents.map(e => Number(e.args.tokenId)))
            : 1000;
          ownedTokenIds = await getOwnedTokenIds(nftInstance, user, maxTokenId);
          break;
        }
      }
    } catch {
      // If balanceOf fails, fallback to scanning all tokenIds
      const maxTokenId = mintEvents.length > 0
        ? Math.max(...mintEvents.map(e => Number(e.args.tokenId)))
        : 1000;
      ownedTokenIds = await getOwnedTokenIds(nftInstance, user, maxTokenId);
    }
    
    // Combine minted and owned tokenIds
    const allUserTokenIds = new Set([...mintedTokenIds, ...ownedTokenIds]);
    
    // Filter retirement events to show any retirement of NFTs the user has interacted with
    const relevantRetirements = retireEvents.filter(e => 
      allUserTokenIds.has(e.args.tokenId.toString())
    );
    
    // Fetch timestamps for retirement events
    const retirementHistoryWithTimestamps = await Promise.all(
      relevantRetirements.map(async (event) => {
        let timestamp = null;
        try {
          if (!window.ethereum) {
            enqueueSnackbar("Web3 wallet not found. Install or enable MetaMask!", { variant: "error" });
            return;
          }
          const provider = new ethers.BrowserProvider(window.ethereum);
          const block = await provider.getBlock(event.blockNumber);
          if (block && block.timestamp) {
            timestamp = block.timestamp;
          }
        } catch (err) {
          console.log("Could not fetch timestamp for retirement event:", err);
        }
        return { ...event, timestamp };
      })
    );
    
    setRetiredEvents(retirementHistoryWithTimestamps);

    // Also prepare full (all-users) retirement history with timestamps for Admin views
    const allRetirementHistoryWithTimestamps = await Promise.all(
      retireEvents.map(async (event) => {
        let timestamp = null;
        try {
          if (!window.ethereum) {
            enqueueSnackbar("Web3 wallet not found. Install or enable MetaMask!", { variant: "error" });
            return;
          }
          const provider = new ethers.BrowserProvider(window.ethereum);
          const block = await provider.getBlock(event.blockNumber);
          if (block && block.timestamp) {
            timestamp = block.timestamp;
          }
        } catch {}
        return { ...event, timestamp };
      })
    );
    setRetiredEventsAll(allRetirementHistoryWithTimestamps);

    // Populate user NFT cards from the canonical loader so null/missing tokenURI
    // NFTs remain visible via placeholder metadata.
    await refreshMintedMetadataList(user);
    
    setMintStatus("Contracts loaded successfully!");
    console.log("All contracts loaded successfully");
    
    } catch (error) {
      const preserveSessionOnError = Boolean(options?.preserveSessionOnError || minting);
      console.error("Error loading contracts:", error);

      if (preserveSessionOnError) {
        const warningMsg = `Contract refresh failed (${error.message}). Keeping current wallet session.`;
        setMintStatus(warningMsg);
        enqueueSnackbar(warningMsg, { variant: "warning" });
        return;
      }

      const failureMsg = `Failed to load contracts: ${error.message}`;
      setMintStatus(failureMsg);
      enqueueSnackbar(failureMsg, { variant: "error" });

      // Reset connection state only for first-load / hard failures.
      setConnected(false);
      setVault(null);
      setNft(null);
      setCarbon(null);
      setCarbonReadAddress("");
      setMarketplace(null);
    }
  }

  async function downloadMetadata(metadata) {
    const jsonString = JSON.stringify(metadata, null, 2);
    return jsonString; 
  }

  async function resolveContributingDevices(metadata, tokenId) {
    const attrs = Array.isArray(metadata?.attributes) ? metadata.attributes : [];
    const getAttr = (name) => attrs.find((a) => a?.trait_type === name)?.value;

    const burnPlanRaw = getAttr("Burn Plan Lots");
    if (!burnPlanRaw) {
      return { available: false, reason: "No Burn Plan Lots found in metadata." };
    }

    const { messageIds, amounts } = parseBurnPlanInput(String(burnPlanRaw));
    if (!carbon) {
      return { available: false, reason: "Carbon contract is not loaded." };
    }

    const lotRows = await Promise.all(messageIds.map(async (id, i) => {
      const rec = await carbon.bridgeMintByMessageId(id);
      const device = String(rec?.device || rec?.[1] || ethers.ZeroAddress);
      const amountWei = BigInt(amounts[i]);
      return {
        messageId: String(id),
        amountWei,
        amountToken: formatUnitsRounded(amountWei, 18, 6),
        device
      };
    }));

    const groupedMap = new Map();
    for (const row of lotRows) {
      const key = String(row.device).toLowerCase();
      const prev = groupedMap.get(key);
      if (!prev) {
        groupedMap.set(key, {
          device: row.device,
          totalWei: row.amountWei,
          totalToken: row.amountToken,
          lotCount: 1,
          messageIds: [row.messageId]
        });
      } else {
        prev.totalWei += row.amountWei;
        prev.totalToken = formatUnitsRounded(prev.totalWei, 18, 6);
        prev.lotCount += 1;
        prev.messageIds.push(row.messageId);
      }
    }

    const groupedDevices = [...groupedMap.values()].sort((a, b) => {
      if (a.totalWei === b.totalWei) return a.device.localeCompare(b.device);
      return a.totalWei > b.totalWei ? -1 : 1;
    });

    const totalWei = lotRows.reduce((sum, r) => sum + r.amountWei, 0n);
    const totalToken = formatUnitsRounded(totalWei, 18, 6);

    const coder = ethers.AbiCoder.defaultAbiCoder();
    const computedProvenanceRoot = ethers.keccak256(
      coder.encode(["bytes32[]", "uint256[]"], [messageIds, amounts])
    );

    const metadataProvenanceRoot = String(getAttr("Mint ProvenanceRoot") || "");
    const metadataRootMatches = metadataProvenanceRoot
      ? metadataProvenanceRoot.toLowerCase() === computedProvenanceRoot.toLowerCase()
      : null;

    let certificatePayloadHash = "";
    let certificateMessageId = "";
    let certificatePrimaryDevice = "";
    let chainRootMatches = null;

    if (nft && tokenId !== null && tokenId !== undefined && tokenId !== "") {
      try {
        const [partA, partB, partC] = await Promise.all([
          nft.getCertificatePartA(tokenId),
          nft.getCertificatePartB(tokenId),
          nft.getCertificatePartC(tokenId)
        ]);
        certificatePrimaryDevice = String(partA?.device || partA?.[2] || "");
        certificateMessageId = String(partB?.messageId || partB?.[3] || "");
        certificatePayloadHash = String(partC?.payloadHash || partC?.[0] || "");
        if (certificatePayloadHash) {
          chainRootMatches = certificatePayloadHash.toLowerCase() === computedProvenanceRoot.toLowerCase();
        }
      } catch (e) {
        console.warn("[resolveContributingDevices] Could not read certificate parts:", e);
      }
    }

    return {
      available: true,
      lotRows,
      groupedDevices,
      totalWei,
      totalToken,
      uniqueDevices: groupedDevices.length,
      computedProvenanceRoot,
      metadataProvenanceRoot,
      metadataRootMatches,
      certificatePayloadHash,
      certificateMessageId,
      certificatePrimaryDevice,
      chainRootMatches
    };
  }

  function handleCloseMetadataDialog() {
    setMetadataDialogOpen(false);
    setMetadataDevicesLoading(false);
    setMetadataDevicesError("");
    setMetadataDevicesReport(null);
  }

  async function showMetadataDialog(metadata, uri, tokenId) {
    // Show immediately, then enrich with chain-derived mint evidence.
    const normalized = normalizeMetadataShape(metadata, tokenId);
    setMetadataDialogContent(normalized);
    setMetadataDialogOpen(true);

    setMetadataDevicesLoading(true);
    setMetadataDevicesError("");
    setMetadataDevicesReport(null);
    try {
      const mintEvidence = await resolveMintEvidenceByTokenId(nft, tokenId);
      const enrichedMetadata = applyMintEvidenceToMetadata(normalized, tokenId, mintEvidence);
      const json = await downloadMetadata(enrichedMetadata);
      setMetadataDialogContent(JSON.parse(json));

      const report = await resolveContributingDevices(enrichedMetadata, tokenId);
      setMetadataDevicesReport(report);
      if (!report.available && report.reason) {
        setMetadataDevicesError(report.reason);
      }
    } catch (e) {
      setMetadataDevicesError(e?.shortMessage || e?.message || "Failed to resolve contributing devices");
    } finally {
      setMetadataDevicesLoading(false);
    }
  }

  function handleDownloadImage() {
    if (!metadataDialogContent || !metadataDialogContent.image) return;
    let url = metadataDialogContent.image;
    if (url.startsWith("ipfs://")) {
      url = resolveIpfsUrl(url);
    }
    // Open image in new tab
    window.open(url, "_blank");
  }

  // After contracts are loaded (e.g., in loadContracts or useEffect)
  useEffect(() => {
    async function fetchCertificatePrice() {
      if (nft) {
        const price = await nft.calculateCarbonTokenAmount();
        setCertificatePrice(price);
      }
    }
    fetchCertificatePrice();
  }, [nft]);

  // Listen for Pause/Unpause events
  useEffect(() => {
    if (!vault) return;

    if (!supportsPauseEvents(vault)) {
      return;
    }

    const handlePaused = () => setPaused(true);
    const handleUnpaused = () => setPaused(false);

    vault.on("Paused", handlePaused);
    vault.on("Unpaused", handleUnpaused);

    // Also poll every 30s in case of missed events
    const interval = setInterval(async () => {
      if (!supportsPausedMethod(vault)) return;
      try {
        const isPaused = await vault.paused();
        setPaused(isPaused);
      } catch (e) {
        console.warn('Skipping paused() poll due to call failure:', e?.message || e);
      }
    }, 30000);

    return () => {
      vault.off("Paused", handlePaused);
      vault.off("Unpaused", handleUnpaused);
      clearInterval(interval);
    };
  }, [vault]);

  // Listen to wallet changes: connect/disconnect, network change, account change
  useEffect(() => {
    if (!window.ethereum) return;

    const resetWalletSessionState = ({ status, snackbar, variant = "info" }) => {
      setConnected(false);
      setAccount("");
      setVault(null);
      setNft(null);
      setCarbon(null);
      setCarbonReadAddress("");
      setMarketplace(null);
      setVaultStatus({ deposited: 0, verified: false, minting: false });
      setMintStatus(status);
      if (snackbar) {
        enqueueSnackbar(snackbar, { variant });
      }
    };

    const handleAccountsChanged = async (accounts) => {
      if (accounts.length === 0) {
        resetWalletSessionState({
          status: "Wallet disconnected",
          snackbar: "Wallet disconnected",
          variant: "info"
        });
      } else {
        const nextAccount = accounts[0];
        setAccount(nextAccount);
        setConnected(true);
        setMintStatus("Wallet account changed. Refreshing contracts...");
        enqueueSnackbar("Wallet account changed", { variant: "info" });

        try {
          const nextProvider = new ethers.BrowserProvider(window.ethereum);
          const nextSigner = await nextProvider.getSigner();

          setProvider(nextProvider);
          setSigner(nextSigner);
          await loadContracts(nextProvider, nextSigner, nextAccount, {
            preserveSessionOnError: true
          });
        } catch (err) {
          console.warn("[wallet] account change refresh failed:", err);
          const errMsg = err?.message || String(err);
          setMintStatus(`Wallet account changed, but refresh failed: ${errMsg}`);
          enqueueSnackbar(`Wallet account changed, but refresh failed: ${errMsg}`, { variant: "warning" });
        }

        try {
          await fetchUserLots({ keepSelection: true, forceWalletProvider: true });
        } catch (err) {
          console.warn("[wallet] fetchUserLots after account change failed:", err);
        }
      }
    };

    const handleChainChanged = async (chainId) => {
      setMintStatus(`Network changed (${chainId}). Refreshing wallet session...`);
      enqueueSnackbar("Network changed, refreshing wallet session...", { variant: "info" });

      try {
        const accounts = await window.ethereum.request({ method: "eth_accounts" });
        if (!accounts || accounts.length === 0) {
          resetWalletSessionState({
            status: "Network changed. Wallet not connected.",
            snackbar: "Network changed. Please reconnect wallet.",
            variant: "warning"
          });
          return;
        }

        const nextProvider = new ethers.BrowserProvider(window.ethereum);
        const nextSigner = await nextProvider.getSigner();
        const nextAccount = await nextSigner.getAddress();

        setProvider(nextProvider);
        setSigner(nextSigner);
        setAccount(nextAccount);
        setConnected(true);

        await loadContracts(nextProvider, nextSigner, nextAccount, {
          preserveSessionOnError: true
        });
      } catch (err) {
        console.warn("[wallet] chain change refresh failed:", err);
        const errMsg = err?.message || String(err);
        setMintStatus(`Network changed. Session preserved, refresh failed: ${errMsg}`);
        enqueueSnackbar(`Network changed. Session preserved, refresh failed: ${errMsg}`, {
          variant: "warning"
        });
      }
    };

    const handleConnect = () => {
      setMintStatus("Wallet connected");
      enqueueSnackbar("Wallet connected", { variant: "success" });
      setConnected(true);
    };
    const handleDisconnect = async (error) => {
      console.warn("[wallet] disconnect event:", error);
      setMintStatus("Wallet transport interrupted. Verifying connection...");

      try {
        const accounts = await window.ethereum.request({ method: "eth_accounts" });
        if (accounts && accounts.length > 0) {
          setConnected(true);
          setAccount(accounts[0]);
          setMintStatus("Wallet transport restored.");
          enqueueSnackbar("Wallet transport restored.", { variant: "info" });
          return;
        }
      } catch (probeError) {
        console.warn("[wallet] disconnect probe failed:", probeError);
      }

      resetWalletSessionState({
        status: "Wallet disconnected",
        snackbar: "Wallet disconnected",
        variant: "info"
      });
    };

    window.ethereum.on("accountsChanged", handleAccountsChanged);
    window.ethereum.on("chainChanged", handleChainChanged);
    window.ethereum.on("connect", handleConnect);
    window.ethereum.on("disconnect", handleDisconnect);

    return () => {
      window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
      window.ethereum.removeListener("chainChanged", handleChainChanged);
      window.ethereum.removeListener("connect", handleConnect);
      window.ethereum.removeListener("disconnect", handleDisconnect);
    };
    // eslint-disable-next-line
  }, [provider, signer, fetchUserLots]);

  // On load, check if wallet is already connected
  useEffect(() => {
    async function checkWalletConnection() {
      try {
        if (!window.ethereum) {
          console.log("MetaMask not detected");
          return;
        }

        // Wait a bit for MetaMask to initialize
        await new Promise(resolve => setTimeout(resolve, 100));

        const accounts = await window.ethereum.request({ 
          method: "eth_accounts" 
        });
        
        if (accounts && accounts.length > 0) {
          console.log("Existing wallet connection found, reconnecting...");
          setMintStatus("Reconnecting to wallet...");
          
          const p = new ethers.BrowserProvider(window.ethereum);
          const s = await p.getSigner();
          const user = await s.getAddress();
          
          setProvider(p);
          setSigner(s);
          setAccount(user);
          setConnected(true);
          
          await loadContracts(p, s, user);
          enqueueSnackbar("Wallet reconnected successfully!", { variant: "success" });
        } else {
          console.log("No existing wallet connection found");
          setMintStatus("Please connect your wallet");
        }
      } catch (error) {
        console.error("Error checking wallet connection:", error);
        setMintStatus("Error checking wallet connection");
        setConnected(false);
      }
    }
    
    checkWalletConnection();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (tokenURI && tokenURI.startsWith("ipfs://")) {
      const url = `https://serenity.ing/ipfs/${tokenURI.replace("ipfs://", "")}`;
      fetch(url)
        .then(res => res.json())
        .then(setNftPreview)
        .catch(() => setNftPreview({ error: "Failed to fetch metadata" }));
    }
  }, [tokenURI]);

  // Keep vault status in sync with the active account/contracts.
  useEffect(() => {
    if (!vault || !account) return;
    refreshVaultStatus();
  }, [vault, account, refreshVaultStatus]);

  const listedLockRef = useRef({}); // tokenId => boolean
  const cancedLockRef = useRef({}); // tokenId => boolean

  useEffect(() => {
    if (!vault || !account) return;

    const updateOnEvent = async () => {
      await refreshVaultStatus();
    };

    vault.on("Deposited", updateOnEvent);
    vault.on("Refunded", updateOnEvent);
    vault.on("NFTMinted", updateOnEvent);

    const interval = setInterval(updateOnEvent, 30000);

    return () => {
      vault.off("Deposited", updateOnEvent);
      vault.off("Refunded", updateOnEvent);
      vault.off("NFTMinted", updateOnEvent);
      clearInterval(interval);
    };
  }, [vault, account, refreshVaultStatus]);

  useEffect(() => {
    if (!account) return;

    // Initial load as soon as account is known, then periodic refresh.
    refreshMintedMetadataList(account).catch((err) => {
      console.warn("[marketplace] initial NFT refresh failed:", err?.message || err);
    });

    const interval = setInterval(() => {
      refreshMintedMetadataList(account).catch((err) => {
        console.warn("[marketplace] periodic NFT refresh failed:", err?.message || err);
      });
    }, 30000); // every 30s

    return () => clearInterval(interval);
  }, [account]);

  // Deposit tokens (approve + deposit)
  async function depositTokens() {
    // Check if user is blocked
    if (isUserBlocked(account)) {
      enqueueSnackbar("You are blocked from performing this action. Please contact support if you believe this is an error.", { variant: "error" });
      return;
    }

    if (!vault || !carbon) {
      setMintStatus("Vault or Carbon contract not loaded");
      enqueueSnackbar("Vault or Carbon contract not loaded", { variant: "error" });
      return;
    }
    // --- Enhanced diagnostics & execution ---
    try {
      if (!depositAmount || isNaN(Number(depositAmount)) || Number(depositAmount) <= 0) {
        enqueueSnackbar("Enter a valid positive deposit amount", { variant: "warning" });
        return;
      }

      if (!window.ethereum) {
        enqueueSnackbar("Web3 wallet not found. Install or enable MetaMask!", { variant: "error" });
        return;
      }
      const provider = new ethers.BrowserProvider(window.ethereum);
      const network = await provider.getNetwork();
      const chainId = Number(network.chainId);
      const expectedChainId = Number(process.env.REACT_APP_NETWORK_ID || 0);

      // Basic chain mismatch check
      if (expectedChainId && chainId !== expectedChainId) {
        const msg = `Wrong network. Connected chainId ${chainId}, expected ${expectedChainId}`;
        setMintStatus(msg);
        enqueueSnackbar(msg, { variant: "error" });
        return;
      }

      // Helper to fetch on-chain code length for address
      async function hasCode(address) {
        try {
          const code = await provider.getCode(address);
          return code && code !== "0x";
        } catch { return false; }
      }

      const vaultHasCode = await hasCode(VAULT_ADDRESS);
      const carbonHasCode = await hasCode(CARBON_ADDRESS);
      if (!vaultHasCode) {
        enqueueSnackbar("No contract code found at Vault address", { variant: "error" });
        return;
      }
      if (!carbonHasCode) {
        enqueueSnackbar("No contract code found at Carbon token address", { variant: "error" });
        return;
      }

      const amountWei = parseUnits(depositAmount, 18);

      // Fetch balances & allowance
      let userBalance = null;
      let currentAllowance = null;
      let pausedState = null;
      try { userBalance = await carbon.balanceOf(account); } catch {}
      try { currentAllowance = await carbon.allowance(account, VAULT_ADDRESS); } catch {}
      try { pausedState = await vault.paused(); } catch {}

      const diag = {
        chainId,
        expectedChainId,
        vaultHasCode,
        carbonHasCode,
        pausedState,
        userBalance: userBalance?.toString?.() || null,
        currentAllowance: currentAllowance?.toString?.() || null,
        requestedDeposit: amountWei.toString()
      };
      console.log("[depositTokens] Preflight diagnostics", diag);

      if (pausedState) {
        enqueueSnackbar("Vault is paused. Deposits disabled.", { variant: "error" });
        return;
      }

      if (userBalance && userBalance < amountWei) {
        enqueueSnackbar("Insufficient CARBON balance for deposit", { variant: "error" });
        return;
      }

      // If allowance already sufficient, skip approve
      const needApprove = !currentAllowance || currentAllowance < amountWei;

      // Simulate approve if needed
      if (needApprove) {
        setMintStatus("Simulating approval...");
        try {
          // callStatic style simulation using estimateGas for visibility
          const estGasApprove = await carbon.approve.estimateGas?.(VAULT_ADDRESS, amountWei).catch(()=>null);
          console.log("[depositTokens] Approve gas estimate", estGasApprove?.toString());
        } catch (simErr) {
          console.warn("Approve simulation failed", simErr);
        }

        setMintStatus("Approving tokens...");
        enqueueSnackbar("Approving tokens...", { variant: "info" });
        let approveTx;
        try {
          approveTx = await carbon.approve(VAULT_ADDRESS, amountWei);
        } catch (approveSendErr) {
          console.error("Approve send failed", approveSendErr);
          throw new Error(`Approve failed: ${approveSendErr.reason || approveSendErr.message}`);
        }
        setMintStatus("Waiting for approval confirmation...");
        await approveTx.wait();
      } else {
        console.log("[depositTokens] Existing allowance sufficient. Skipping approve.");
      }

      // Optional: re-check allowance
      try {
        const postAllowance = await carbon.allowance(account, VAULT_ADDRESS);
        console.log("[depositTokens] Post-approve allowance", postAllowance.toString());
      } catch {}

      // Simulate deposit via gas estimate
      setMintStatus("Simulating deposit...");
      let estGasDeposit = null;
      try {
        estGasDeposit = await vault.depositTokens.estimateGas?.(amountWei).catch(()=>null);
        console.log("[depositTokens] Deposit gas estimate", estGasDeposit?.toString());
      } catch (simDepErr) {
        console.warn("Deposit simulation failed", simDepErr);
      }

      setMintStatus("Depositing tokens...");
      enqueueSnackbar("Depositing tokens...", { variant: "info" });

      let depositTx;
      try {
        const gasOpts = estGasDeposit ? { gasLimit: estGasDeposit * 120n / 100n } : {};
        depositTx = await vault.depositTokens(amountWei, gasOpts);
      } catch (depSendErr) {
        console.error("Deposit send failed", depSendErr);
        throw new Error(`Deposit send failed: ${depSendErr.reason || depSendErr.message}`);
      }
      setMintStatus("Waiting for deposit confirmation...");
      await depositTx.wait();

      setMintStatus("Deposit successful!");
      enqueueSnackbar("Tokens deposited successfully!", { variant: "success" });
      await refreshVaultStatus();

    } catch (err) {
      console.error("[depositTokens] Error", err);
      const raw = err?.data || err?.error?.data;
      if (raw && raw.message) console.error("[depositTokens] Low-level data message", raw.message);
      const friendly = err.reason || err.message || "Unknown deposit error";
      setMintStatus("Deposit failed: " + friendly);
      enqueueSnackbar("Deposit failed: " + friendly, { variant: "error" });
    }
  }

  // Diagnostic helper (can be invoked from console via window.diagnoseDeposit())
  if (typeof window !== 'undefined' && !window.diagnoseDeposit) {
    window.diagnoseDeposit = async () => {
      if (!vault || !carbon) {
        return { error: 'Contracts not loaded' };
      }
      try {
        if (!window.ethereum) {
          enqueueSnackbar("Web3 wallet not found. Install or enable MetaMask!", { variant: "error" });
          return { error: 'No wallet' };
        }
        const provider = new ethers.BrowserProvider(window.ethereum);
        const network = await provider.getNetwork();
        const chainId = Number(network.chainId);
        const balance = await carbon.balanceOf(account);
        const allowance = await carbon.allowance(account, VAULT_ADDRESS);
        const pausedState = await vault.paused();
        return {
          chainId,
            expectedChainId: Number(process.env.REACT_APP_NETWORK_ID || 0),
            balance: balance.toString(),
            allowance: allowance.toString(),
            paused: pausedState,
            account,
            VAULT_ADDRESS,
            CARBON_ADDRESS
        };
      } catch (e) {
        return { error: e.message };
      }
    };
  }

  // Claim refund
  async function claimRefund() {
    // Check if user is blocked
    if (isUserBlocked(account)) {
      enqueueSnackbar("You are blocked from performing this action. Please contact support if you believe this is an error.", { variant: "error" });
      return;
    }

    if (!vault) {
      setMintStatus("Vault contract not loaded");
      enqueueSnackbar("Vault contract not loaded", { variant: "error" });
      return;
    }

    const depositedWei = BigInt(vaultStatus?.deposited || 0);
    if (depositedWei <= 0n) {
      setMintStatus("No deposited balance to refund");
      enqueueSnackbar("No deposited balance to refund", { variant: "warning" });
      return;
    }

    try {
      setMintStatus("Claiming full refund...");
      enqueueSnackbar("Claiming full refund (entire deposited amount)...", { variant: "info" });
      let approveTx = await vault.claimRefund(depositedWei);
      setMintStatus("Waiting for refund transaction confirmation...");
      enqueueSnackbar("Waiting for refund transaction confirmation...", { variant: "info" });
      await approveTx.wait();
      setMintStatus("Refund claimed!");
      enqueueSnackbar("Refund claimed successfully!", { variant: "success" });
      await refreshVaultStatus();
    } catch (err) {
      setMintStatus("Refund failed: " + (err.reason || err.message));
      enqueueSnackbar("Refund failed: " + (err.reason || err.message), { variant: "error" });
    }
  }

  // Retire NFT (all logic in handler)
  async function retireSNPF(tokenId) {
    if (!vault || !nft) {
      setMintStatus("Contracts not loaded");
      enqueueSnackbar("Contracts not loaded", { variant: "error" });
      return;
    }

    try {
      setMintStatus("Updating retirement metadata and uploading to IPFS...");
      enqueueSnackbar("Preparing retirement certificate for NFT #" + tokenId + "...", { variant: "info" });

      // marketplace: ethers.Contract instance
      let priceEth = "unknown";
      let auctioned = "false";

      /* uncoment when new marketplace contract with indexed events deployed!
      const events = await marketplace.queryFilter(
        marketplace.filters.TokenPurchased(tokenId),
        0,
        "latest"
      );
      if (events.length > 0) {
        // Get the last purchase (most recent)
        const lastPurchase = events[events.length - 1];
        const priceWei = lastPurchase.args.price;
        priceEth = ethers.formatEther(priceWei);
        console.log(`Token #${tokenId} was last purchased for ${priceEth} ETH`);
      } else {
        console.log("No purchase found for this token");

        const events = await marketplace.queryFilter(
          marketplace.filters.AuctionEnded(tokenId),
          0,
          "latest"
        );
        if (events.length > 0) {
          // Get the last auction (most recent)
          const lastAuction = events[events.length - 1];
          const priceWei = lastAuction.args.finalPrice;
          priceEth = ethers.formatEther(priceWei);
          auctioned = "true";
          console.log(`Token #${tokenId} was last auctioned for ${priceEth} ETH`);
        } else {
          console.log("No auction found for this token");
          priceEth = "unknown";
        }
      }
      */


      // Fetch ACCU spot price from backend proxy (server-side fetch avoids browser CORS)
      let price = 38.5; // default fallback
      let source = undefined;
      try {
        const backendBase = process.env.REACT_APP_RETIREMENT_API || 'https://serenity.ing/retirement';
        const resp = await fetch(`${backendBase}/api/accu/spot`, { method: 'GET' });
        if (resp.ok) {
          const data = await resp.json();
          if (data && data.success && typeof data.price === 'number') {
            price = Number(data.price);
            source = data.source || undefined;
          }
        }
      } catch (e) {
        // Keep fallback silently if backend not reachable
      }


      // Get current metadata from the NFT
      const currentURI = await nft.tokenURI(tokenId);
      const currentUrl = resolveIpfsUrl(currentURI);
      const currentResponse = await fetch(currentUrl);
      const currentMetadata = await currentResponse.json();

      const mintEvidence = await resolveMintEvidenceByTokenId(nft, tokenId);
      const metadataWithMintEvidence = applyMintEvidenceToMetadata(currentMetadata, tokenId, mintEvidence);

      // Get retirement details from contract
      const retirementDetails = await nft.getRetirementDetails(tokenId);
      const retiredBy = retirementDetails.verifier;
      const retiredTimestamp = Number(retirementDetails.timestamp);

      // Update metadata with retirement information
      const updatedMetadata = {
        ...metadataWithMintEvidence,
        name: String(metadataWithMintEvidence.name || `Carbon Credit Certificate #${tokenId}`).replace("Certificate", "RETIRED Certificate"),
        description: "This NFT certifies the RETIREMENT of carbon offset tokens equivalent to 1 ton of CO₂. This certificate has been permanently retired and cannot be traded.",
        attributes: [
          ...metadataWithMintEvidence.attributes.filter(attr => attr.trait_type !== "Retired"),
          { trait_type: "Retired", value: "true" },
          { trait_type: "Auctioned", value: auctioned },
          { trait_type: "Price", value: price.toString() },
          { trait_type: "Source", value: source },
          { trait_type: "Retired By", value: retiredBy },
          { trait_type: "Retirement Date", value: new Date(retiredTimestamp * 1000).toISOString() },
          { trait_type: "Retirement Timestamp", value: retiredTimestamp.toString() },
          { trait_type: "Status", value: "PERMANENTLY RETIRED" }
        ]
      };

      // Upload updated metadata to IPFS
      let ipfsResponse = null;
      try {
        const postBody = JSON.stringify(updatedMetadata);
        const res = await fetch("https://serenity.ing/nftapi/create-nft-folder", {  
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: postBody
        });
        
        if (!res.ok) throw new Error("Failed to upload retirement metadata to backend");
        ipfsResponse = await res.json();

        if (ipfsResponse.success && Number(ipfsResponse.folder_id) === Number(tokenId)) {
          const folderHash = ipfsResponse.folder_ipfs;
          const newTokenURI = `ipfs://${folderHash}/metadata.json`;
          
          setMintStatus("Retirement metadata uploaded to IPFS successfully!");
          enqueueSnackbar("Retirement certificate generated and uploaded to IPFS!", { variant: "success" });
          
          // UPDATE THE TOKEN URI ON-CHAIN
          const signer = await provider.getSigner();
          const nftWithSigner = nft.connect(signer);
          const updateTx = await nftWithSigner.updateTokenURI(tokenId, newTokenURI);
          await updateTx.wait();
          
          setMintStatus("Retirement certificate updated successfully!");
          enqueueSnackbar("Retirement certificate updated on blockchain!", { variant: "success" });
          
          console.log("New retirement certificate URI:", newTokenURI);
        } else {
          throw new Error("IPFS upload response invalid");
        }

      } catch (err) {
        setMintStatus("Failed to upload retirement metadata: " + (err.message || err));
        enqueueSnackbar("Failed to upload retirement metadata: " + (err.message || err), { variant: "error" });
        console.error("Retirement metadata upload error:", err);
      }

    } catch (err) {
      setMintStatus("Retirement metadata update failed: " + (err.reason || err.message));
      enqueueSnackbar("Retirement metadata update failed: " + (err.reason || err.message), { variant: "error" });
    }
  }

  // Mint NFT (all logic in handler)
  async function mintNFT(options = {}) {
      console.log("[mintNFT] Handler called");
    // Check if user is blocked
    if (isUserBlocked(account)) {
      enqueueSnackbar("You are blocked from performing this action. Please contact support if you believe this is an error.", { variant: "error" });
      return;
    }


    try {
      // --- Progress: Step 1 ---
      if (useV2Mint) {
        // Enforce user confirmation of burn plan (and prevent minting if plan changed after confirm)
        const approved = options.v2ApprovedSnapshot ?? v2PlanApprovedSnapshot;
        if (!approved || approved !== String(v2BurnPlanInput || "")) {
          setMintStatus("Please preview and confirm the V2 burn plan before minting.");
          enqueueSnackbar("Please preview and confirm the V2 burn plan before minting.", { variant: "warning" });
          return;
        }
      }

      setMinting(true);

      if (!vault || !nft) {
        setMintStatus("Contracts not loaded yet");
        enqueueSnackbar("Contracts not loaded yet", { variant: "error" });
        setMinting(false);
        return;
      }

      if (useV2Mint && !carbon) {
        setMintStatus("Carbon token contract not loaded yet (required for V2 mint)");
        enqueueSnackbar("Carbon token contract not loaded yet (required for V2 mint)", { variant: "error" });
        setMinting(false);
        return;
      }

      // --- Progress: Step 2 ---
      setMintStatus("Checking how many certificates will be minted...");
      enqueueSnackbar("Checking how many certificates will be minted...", { variant: "info" });
      let lastTokenId = 0;
      let mintEvents = [];
      try {
        mintEvents = await nft.queryFilter("CarbonCreditMinted", 0, "latest");
        if (mintEvents.length > 0) {
          lastTokenId = Number(mintEvents[mintEvents.length - 1].args.tokenId);
        }
      } catch (err) {
        console.warn("Could not fetch last tokenId, defaulting to 0", err);
      }
      const nextTokenId = lastTokenId + 1;

      // --- Progress: Step 3 ---
      setMintStatus(`Preparing metadata for certificate #${nextTokenId}...`);
      enqueueSnackbar(`Preparing metadata for certificate #${nextTokenId}...`, { variant: "info" });
      let deviceId = account;
      const timestamp = Math.floor(Date.now() / 1000);

      // For V2 mints, prefer the device from the first selected messageId lot
      // so initial metadata.json (the one referenced by tokenURI) is already correct.
      let v2BurnPlan = null;
      if (useV2Mint) {
        try {
          v2BurnPlan = parseBurnPlanInput(v2BurnPlanInput);
          if (carbon && v2BurnPlan.messageIds.length > 0) {
            const rec = await carbon.bridgeMintByMessageId(v2BurnPlan.messageIds[0]);
            const deviceFromChain = rec?.device || rec?.[1];
            if (deviceFromChain && String(deviceFromChain) !== ethers.ZeroAddress) {
              deviceId = String(deviceFromChain);
            }
          }
        } catch (e) {
          console.warn("[mintNFT] Could not resolve device from V2 burn plan, using account as fallback:", e);
        }
      }

      let metadata = {
        name: `Carbon Credit Certificate #${nextTokenId}`,
        description: "This NFT certifies the retirement of carbon offset tokens equivalent to 1 ton of CO₂. Issued by an approved renewable energy device.",
        image: "ipfs://",
        attributes: [
          { trait_type: "Device ID", value: deviceId },
          { trait_type: "Retired", value: "false" },
          { trait_type: "Timestamp", value: timestamp },
          { trait_type: "Issuer ID", value: account },
          { trait_type: "Issuer", value: "Ergon Energy On the Go Pty Ltd" },
          { trait_type: "ID", value: nextTokenId.toString() }
        ]
      };

      // Pre-compute deterministic mint metadata BEFORE IPFS upload.
      try {
        const requiredBurn = BigInt(await nft.calculateCarbonTokenAmount());
        metadata.attributes = upsertAttribute(metadata.attributes, "Carbon Tokens Burned", requiredBurn.toString());
        if (useV2Mint) {
          const { messageIds, amounts } = v2BurnPlan || parseBurnPlanInput(v2BurnPlanInput);
          const coder = ethers.AbiCoder.defaultAbiCoder();
          const encoded = coder.encode(["bytes32[]", "uint256[]"], [messageIds, amounts]);
          const provenanceRootPreview = ethers.keccak256(encoded);
          metadata.attributes = upsertAttribute(metadata.attributes, "Mint Type", "V2");
          metadata.attributes = upsertAttribute(metadata.attributes, "Mint MessageId", messageIds[0]);
          metadata.attributes = upsertAttribute(metadata.attributes, "Mint ProvenanceRoot", provenanceRootPreview);

          // Add full burn plan breakdown as a new attribute
          const burnPlanLots = messageIds.map((id, idx) => ({
            messageId: id,
            amount: amounts[idx]?.toString?.() || "0"
          }));
          metadata.attributes = upsertAttribute(
            metadata.attributes,
            "Burn Plan Lots",
            JSON.stringify(burnPlanLots)
          );
        } else {
          metadata.attributes = upsertAttribute(metadata.attributes, "Mint Type", "V1");
        }
      } catch (e) {
        console.warn("[mintNFT] Could not pre-compute mint metadata:", e);
      }

      // Mint tx hash is unknown before transaction submission. Keep a placeholder
      // so the tokenURI metadata still contains this field on initial upload.
      metadata.attributes = upsertAttribute(metadata.attributes, "Mint Tx Hash", "PENDING");
      metadata.attributes = upsertAttribute(metadata.attributes, "Mint Block Number", "PENDING");

      // Validate metadata completeness before backend submission
      function validateMetadata(meta) {
        if (!meta.name || !meta.description || !meta.image || !Array.isArray(meta.attributes)) return false;
        const requiredTraits = [
          "Device ID",
          "Retired",
          "Timestamp",
          "Issuer ID",
          "Issuer",
          "ID",
          "Mint Type",
          "Mint MessageId",
          "Mint ProvenanceRoot",
          "Burn Plan Lots"
        ];
        for (const trait of requiredTraits) {
          if (!meta.attributes.some(attr => attr.trait_type === trait && attr.value)) return false;
        }
        return true;
      }
      if (!validateMetadata(metadata)) {
        setMintStatus("Metadata validation failed: missing required fields");
        enqueueSnackbar("Metadata validation failed: missing required fields", { variant: "error" });
        setMinting(false);
        console.error("[mintNFT] Metadata validation failed:", metadata);
        return;
      }
      console.log("[mintNFT] Metadata to send to backend:", metadata);

      // --- Progress: Step 4 ---
      setMintStatus("Uploading metadata to IPFS...");
      enqueueSnackbar("Uploading metadata to IPFS...", { variant: "info" });
      let ipfsResponse = null;
      let tokenURI = "";
      try {
        const postBody = JSON.stringify(metadata);
        const res = await fetch("https://serenity.ing/nftapi/create-nft-folder", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: postBody
        });
        console.log("[mintNFT] IPFS response status:", res.status);
        if (!res.ok) {
          let backendMessage = "Failed to upload metadata to backend";
          try {
            const errJson = await res.json();
            backendMessage = errJson?.error || errJson?.message || backendMessage;
          } catch {}
          throw new Error(backendMessage);
        }
        ipfsResponse = await res.json();
        console.log("[mintNFT] IPFS response body:", ipfsResponse);
        if (ipfsResponse.success && ipfsResponse.folder_ipfs) {
          tokenURI = `ipfs://${ipfsResponse.folder_ipfs}/metadata.json`;
        } else {
          throw new Error("IPFS upload response invalid");
        }
      } catch (err) {
        setMintStatus("Failed to upload metadata to backend: " + (err.message || err));
        enqueueSnackbar("Failed to upload metadata to backend: " + (err.message || err), { variant: "error" });
        setMinting(false);
        return;
      }

      // --- Progress: Step 5 ---
      setMintStatus("Minting NFT on blockchain (this may take a while if burning multiple lots)...");
      enqueueSnackbar("Minting NFT on blockchain (this may take a while if burning multiple lots)...", { variant: "info" });
      let tx;

      if (useV2Mint) {
        // V2 requires a burn plan (messageIds+amounts) and typically requires an allowance to the vault.
        const requiredBurn = await nft.calculateCarbonTokenAmount();
        const { messageIds, amounts } = parseBurnPlanInput(v2BurnPlanInput);
        const planned = amounts.reduce((acc, a) => acc + a, 0n);
        if (planned !== requiredBurn) {
          setMintStatus(`V2 burn plan sum (${planned}) != required (${requiredBurn}). Use wei amounts or decimals in token units.`);
          enqueueSnackbar(`V2 burn plan sum (${planned}) != required (${requiredBurn}). Use wei amounts or decimals in token units.`, { variant: "error" });
          setMinting(false);
          return;
        }

        setMintStatus(`Approving CARBON spend to vault for ${formatUnitsRounded(requiredBurn, 18, 5)} tokens...`);
        enqueueSnackbar(`Approving CARBON spend to vault for ${formatUnitsRounded(requiredBurn, 18, 5)} tokens...`, { variant: "info" });
        const vaultAddrToApprove = vault?.target || VAULT_ADDRESS;
        const approveTx = await carbon.approve(vaultAddrToApprove, requiredBurn);
        await approveTx.wait();

        const sourceEmitterEnv = (
          process.env.REACT_APP_SOURCE_BRIDGEEMITTER_CONTRACT_ADDRESS ||
          process.env.REACT_APP_SOURCE_BRIDGE_EMITTER_ADDRESS ||
          process.env.REACT_APP_SOURCE_BRIDGE_EMITTER ||
          process.env.REACT_APP_BRIDGE_EMITTER_ADDRESS ||
          ""
        ).trim();

        let sourceChainId = null;
        const sourceChainIdEnv = (
          process.env.REACT_APP_SOURCE_CHAIN_ID ||
          process.env.REACT_APP_SRC_CHAIN_ID ||
          ""
        ).trim();

        if (sourceChainIdEnv) {
          try {
            const parsed = BigInt(sourceChainIdEnv);
            if (parsed >= 0n) sourceChainId = parsed;
          } catch {}
        }

        if (sourceChainId === null) {
          const sourceRpcUrl = (
            process.env.REACT_APP_SOURCE_RPC_URL ||
            process.env.REACT_APP_SRC_RPC_URL ||
            ""
          ).trim();
          if (sourceRpcUrl) {
            try {
              const sourceProvider = new ethers.JsonRpcProvider(sourceRpcUrl);
              const net = await sourceProvider.getNetwork();
              if (net?.chainId != null) sourceChainId = BigInt(net.chainId);
            } catch {}
          }
        }

        if (sourceChainId === null) {
          const msg = "Cannot resolve source chain id. Set REACT_APP_SOURCE_CHAIN_ID (for example 1234) or REACT_APP_SOURCE_RPC_URL.";
          setMintStatus(msg);
          enqueueSnackbar(msg, { variant: "error" });
          setMinting(false);
          return;
        }

        const sourceEmitter = ethers.isAddress(sourceEmitterEnv)
          ? sourceEmitterEnv
          : ethers.ZeroAddress;

        const req = {
          tokenURI,
          messageIds,
          amounts,
          carbonPriceAtMint: 0n,
          royaltyBps: 0,
          schemaHash: ethers.ZeroHash,
          deviceDataHash: ethers.ZeroHash,
          calcHash: ethers.ZeroHash,
          srcChainId: sourceChainId,
          srcEmitter: sourceEmitter,
          payloadHash: ethers.ZeroHash,
          v1TxHash: ethers.ZeroHash,
          v1BlockNumber: 0n
        };

        setMintStatus(`Calling vault.mintNFTIfEligibleV2 for ${messageIds.length} lot(s)...`);
        enqueueSnackbar(`Calling vault.mintNFTIfEligibleV2 for ${messageIds.length} lot(s)...`, { variant: "info" });
        tx = await vault.mintNFTIfEligibleV2(req);
      } else {
        setMintStatus("Calling vault.mintNFTIfEligible...");
        enqueueSnackbar("Calling vault.mintNFTIfEligible...", { variant: "info" });
        tx = await vault.mintNFTIfEligible(tokenURI, account);
      }
      setMintStatus("Waiting for transaction confirmation...");
      enqueueSnackbar("Waiting for transaction confirmation...", { variant: "info" });
      const receipt = await tx.wait();

      // 4. Parse receipt logs (NFT + Vault) to extract tokenId and V2 fields
      let tokenId = null;
      let burnedAmount = null;
      let messageId = null;
      let provenanceRoot = null;
      let sawMintV2 = false;
      let mintedUriFromEvent = null;
      let mintedDeviceFromEvent = null;

      const normalizeAddr = (a) => (a ? String(a).toLowerCase() : "");
      const nftAddr = normalizeAddr(nft?.target || NFT_ADDRESS);
      const vaultAddr = normalizeAddr(vault?.target || VAULT_ADDRESS);

      for (const log of receipt.logs) {
        const logAddr = normalizeAddr(log?.address);
        let parsed = null;

        try {
          if (logAddr === nftAddr) {
            parsed = nft.interface.parseLog(log);
          } else if (logAddr === vaultAddr) {
            parsed = vault.interface.parseLog(log);
          } else {
            continue;
          }
        } catch (err) {
          // Not an event we recognize for this interface
          continue;
        }

        console.log("[mintNFT] Parsed event:", parsed.name, parsed.args);

        if (parsed.name === "CarbonCreditMinted") {
          tokenId = parsed.args.tokenId.toString();
          burnedAmount = parsed.args.burnedAmount;
          mintedUriFromEvent = parsed.args.uri;
          mintedDeviceFromEvent = parsed.args.device;
          console.log(`[mintNFT] Found tokenId=${tokenId} burnedAmount=${burnedAmount}`);
        }

        // Vault V2 event contains provenanceRoot, but not messageId
        if (parsed.name === "NFTMintedV2") {
          tokenId = parsed.args.tokenId.toString();
          burnedAmount = parsed.args.burnedAmount;
          provenanceRoot = String(parsed.args.provenanceRoot);
          console.log(`[mintNFT] Found provenanceRoot (vault): ${provenanceRoot}`);
        }

        // NFT V2 event contains provenanceRoot + messageId
        if (parsed.name === "CarbonCreditMintedV2") {
          sawMintV2 = true;
          tokenId = parsed.args.tokenId.toString();
          burnedAmount = parsed.args.burnedAmount;
          provenanceRoot = String(parsed.args.provenanceRoot);
          messageId = String(parsed.args.messageId);
          console.log(`[mintNFT] Found provenanceRoot (nft): ${provenanceRoot}`);
          console.log(`[mintNFT] Found messageId: ${messageId}`);
        }

        // Stop early once we have the V2 fields
        if (tokenId && burnedAmount && sawMintV2 && mintedDeviceFromEvent) {
          break;
        }
      }
      if (!tokenId) {
        setMintStatus("Failed to parse tokenId from mint event");
        enqueueSnackbar("Failed to parse tokenId from mint event", { variant: "error" });
        setMinting(false);
        return;
      }

      // 5. Update metadata with tokenId and other info
      metadata.name = `Carbon Credit Certificate #${tokenId}`;
      metadata.attributes = metadata.attributes.filter(attr => attr.trait_type !== "ID");
      metadata.attributes.push({ trait_type: "ID", value: tokenId.toString() });
      if (mintedDeviceFromEvent) {
        metadata.attributes = metadata.attributes.filter(attr => attr.trait_type !== "Device ID");
        metadata.attributes.push({ trait_type: "Device ID", value: String(mintedDeviceFromEvent) });
      }
      if (burnedAmount) metadata.attributes = upsertAttribute(metadata.attributes, "Carbon Tokens Burned", burnedAmount.toString());
      if (messageId) metadata.attributes = upsertAttribute(metadata.attributes, "Mint MessageId", messageId);
      if (provenanceRoot) metadata.attributes = upsertAttribute(metadata.attributes, "Mint ProvenanceRoot", provenanceRoot);
      // These are useful in the UI, but note: they are NOT visible via tokenURI unless tokenURI is updated (which is restricted).
      metadata.attributes = upsertAttribute(metadata.attributes, "Mint Tx Hash", tx.hash);
      metadata.attributes = upsertAttribute(metadata.attributes, "Mint Block Number", receipt.blockNumber?.toString());

      // Upload post-mint metadata and try to sync tokenURI to it when contract rules allow.
      let finalTokenURI = mintedUriFromEvent || tokenURI;
      const postMintTokenURI = await postFullMetadataAfterMint(tokenId, metadata);
      if (postMintTokenURI && postMintTokenURI !== finalTokenURI) {
        try {
          setMintStatus("Updating on-chain tokenURI to post-mint metadata...");
          enqueueSnackbar("Updating on-chain tokenURI to post-mint metadata...", { variant: "info" });
          const signer = await provider.getSigner();
          const nftWithSigner = nft.connect(signer);
          const updateUriTx = await nftWithSigner.updateTokenURI(tokenId, postMintTokenURI);
          await updateUriTx.wait();
          finalTokenURI = postMintTokenURI;
          setMintStatus("Post-mint tokenURI updated successfully!");
          enqueueSnackbar("Post-mint tokenURI updated successfully!", { variant: "success" });
        } catch (uriErr) {
          console.warn("[mintNFT] Could not update tokenURI after mint:", uriErr);
          enqueueSnackbar(
            "Post-mint metadata uploaded, but on-chain tokenURI update was not permitted on this contract.",
            { variant: "warning" }
          );
        }
      }

      // Validate that 'ID' is present in metadata after mint
      if (!metadata.attributes.some(attr => attr.trait_type === "ID" && attr.value)) {
        setMintStatus("Metadata validation failed: missing 'ID' after mint");
        enqueueSnackbar("Metadata validation failed: missing 'ID' after mint", { variant: "error" });
        setMinting(false);
        console.error("[mintNFT] Metadata missing 'ID' after mint:", metadata);
        return;
      }

  // POST full metadata after mint to backend (overwrite metadata.json)
  async function postFullMetadataAfterMint(tokenId, metadata) {
    setMintStatus("Uploading full metadata to IPFS...");
    enqueueSnackbar("Uploading full metadata to IPFS...", { variant: "info" });
    try {
      // Send full metadata to backend for overwrite
      const res = await fetch("https://serenity.ing/nftapi/create-nft-folder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(metadata)
      });
      if (!res.ok) {
        let backendMessage = "Failed to upload full metadata to backend";
        try {
          const errJson = await res.json();
          backendMessage = errJson?.error || errJson?.message || backendMessage;
        } catch {}
        throw new Error(backendMessage);
      }
      const ipfsResponse = await res.json();
      console.log("[postFullMetadataAfterMint] IPFS response:", ipfsResponse);
      if (ipfsResponse.success && ipfsResponse.folder_ipfs) {
        const postMintTokenURI = `ipfs://${ipfsResponse.folder_ipfs}/metadata.json`;
        setMintStatus("Full metadata uploaded to IPFS!");
        enqueueSnackbar("Full metadata uploaded to IPFS!", { variant: "success" });
        return postMintTokenURI;
      } else {
        throw new Error("IPFS upload response invalid");
      }
    } catch (err) {
      setMintStatus("Failed to upload full metadata after mint: " + (err.message || err));
      enqueueSnackbar("Failed to upload full metadata after mint: " + (err.message || err), { variant: "error" });
      console.error("[postFullMetadataAfterMint] Error:", err);
      return "";
    }
  }

      // 6. Royalty info
      try {
        let [recipient, percentage] = await nft.getRoyaltyInfo(tokenId);
        percentage = Number(percentage) / 100; // Convert basis points to percentage
        console.log("royalty : " + recipient + " " + percentage.toString() + "%");
        setMintStatus("royalty : " + recipient + " " + percentage.toString() + "%");
        enqueueSnackbar(`Royalty granted to ${recipient} at ${percentage.toString()}%`, { variant: "success" });
      } catch (err) {
        setMintStatus("Granting royalty failed: " + (err.reason || err.message));
        enqueueSnackbar("Granting royalty failed: " + (err.reason || err.message), { variant: "error" });
      }

      setMintedTokenId(tokenId);
      setMintedTokenURI(finalTokenURI);
      setMintedMetadata(metadata);

      // Refresh lot balances immediately after mint confirmation so V2 dialog
      // shows up-to-date balances without requiring a full page reload.
      if (useV2Mint) {
        try {
          const { messageIds, amounts } = parseBurnPlanInput(v2BurnPlanInput);
          applyV2BurnPlanLocally(messageIds, amounts);
        } catch (e) {
          console.warn("[mintNFT] Could not apply local lot burn plan update:", e);
        }
        await fetchUserLots({ keepSelection: false, forceWalletProvider: true });
      }

      setMintStatus("NFT minted successfully!");
      enqueueSnackbar("NFT minted successfully!", { variant: "success" });

    } catch (err) {
      setMintStatus("Minting failed: " + (err.reason || err.message));
      enqueueSnackbar("Minting failed: " + (err.reason || err.message), { variant: "error" });
    }
    setMinting(false);
  }

  // Retire NFT (all logic in handler)
  async function retireNFT(tokenId) {
    // Check if user is blocked
    if (isUserBlocked(account)) {
      enqueueSnackbar("You are blocked from performing this action. Please contact support if you believe this is an error.", { variant: "error" });
      return;
    }

    if (!nft) {
      setMintStatus("NFT contract not loaded");
      enqueueSnackbar("NFT contract not loaded", { variant: "error" });
      return;
    }
    
    try {
      setMintStatus("Retiring carbon credit...");
      enqueueSnackbar("Retiring carbon credit...", { variant: "info" });
      
      const signer = await provider.getSigner();
      const nftWithSigner = nft.connect(signer);
      
      const tx = await nftWithSigner.retireNFT(tokenId);
      setMintStatus("Waiting for retirement transaction confirmation...");
      enqueueSnackbar("Processing retirement...", { variant: "info" });
      
      await tx.wait();
      
      setMintStatus("Carbon credit retired successfully!");
      enqueueSnackbar("Carbon credit retired!", { variant: "success" });

      await retireSNPF(tokenId);

      // Refresh NFT list to show updated retirement status
      await refreshMintedMetadataList();
      
    } catch (err) {
      setMintStatus("Retirement failed: " + (err.reason || err.message));
      enqueueSnackbar("Retirement failed: " + (err.reason || err.message), { variant: "error" });
    }
  }

  // Admin actionss
  async function cancelMint() {
    if (!vault) {
      setMintStatus("Vault contract not loaded");
      enqueueSnackbar("Vault contract not loaded", { variant: "error" });
      return;
    }
    await vault.cancelMintRequest();
  }

  // Handler for dialog confirm verified users
  const handleVerifyDialogConfirm = async () => {
    setVerifyLoading(true);
    try {
      await verifyUsers([verifyAddress], verifyAction);
      
      // Only update UI after transaction is confirmed
      setMintStatus(
        verifyAction
          ? `User ${verifyAddress} verified!`
          : `User ${verifyAddress} unverified!`
      );
      enqueueSnackbar(
        verifyAction
          ? `User ${verifyAddress} verified successfully!`
          : `User ${verifyAddress} unverified successfully!`,
        { variant: "success" }
      );
      setVerifyDialogOpen(false);
      setVerifyAddress("");
      
      // Refresh vault status to reflect changes
      await refreshVaultStatus();
      
    } catch (err) {
      setMintStatus("Verification failed: " + (err.reason || err.message));
      enqueueSnackbar("Verification failed: " + (err.reason || err.message), { variant: "error" });
    }
    setVerifyLoading(false);
  };

  async function verifyUsers(users, status) {
    if (!vault) {
      setMintStatus("Vault contract not loaded");
      enqueueSnackbar("Vault contract not loaded", { variant: "error" });
      return;
    }
    
    console.log(`Setting verification status for users:`, users, `to:`, status);
    
    // Wait for transaction to be mined
    const tx = await vault.setVerifiedUsers(users, status);
    console.log("Verification transaction sent:", tx.hash);
    
    // Wait for confirmation
    const receipt = await tx.wait();
    console.log("Verification transaction confirmed:", receipt.transactionHash);
    
    return receipt;
  }

  async function pause() {
    if (!vault) {
      setMintStatus("Vault contract not loaded");
      enqueueSnackbar("Vault contract not loaded", { variant: "error" });
      return;
    }
    await vault.pause();
  }

  async function unpause() {
    if (!vault) {
      setMintStatus("Vault contract not loaded");
      enqueueSnackbar("Vault contract not loaded", { variant: "error" });
      return;
    }
    await vault.unpause();    
  }

  // Pause/Unpause Marketplace
  async function pauseMarketplace() {
    if (!marketplace) return;
    try {
      await marketplace.pause();
      setMarketplacePaused(true);
      enqueueSnackbar("Marketplace paused", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Failed to pause marketplace: " + (err.reason || err.message), { variant: "error" });
    }
  }

  async function unpauseMarketplace() {
    if (!marketplace) return;
    try {
      await marketplace.unpause();
      setMarketplacePaused(false);
      enqueueSnackbar("Marketplace unpaused", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Failed to unpause marketplace: " + (err.reason || err.message), { variant: "error" });
    }
  }

  // Update Carbon Price
  async function handleUpdateCarbonPriceConfirm() {
    if (!nft) return;
    try {
      enqueueSnackbar("Carbon price updated not allowed", { variant: "info" });
      setUpdateCarbonPriceDialogOpen(false);
      setCarbonPriceInput("");
      return;
    } catch (err) {
      enqueueSnackbar("Failed to update carbon price: " + (err.reason || err.message), { variant: "error" });
    }
  }

  // Update Ergon Price
  async function handleUpdateErgonPriceConfirm() {
    if (!nft) return;
    try {
      enqueueSnackbar("Ergon price updated not allowed", { variant: "info" });
      setUpdateErgonPriceDialogOpen(false);
      setErgonPriceInput("");
      return;
    } catch (err) {
      enqueueSnackbar("Failed to update ergon price: " + (err.reason || err.message), { variant: "error" });
    }
  }

// Robust admin detection with retry logic
  async function checkAdminStatus(vaultInstance, userAddress, maxRetries = 3) {
    let attempts = 0;
    
    while (attempts < maxRetries) {
      try {
        console.log(`Checking admin status (attempt ${attempts + 1}/${maxRetries})...`);
        
        // Add a small delay for subsequent attempts
        if (attempts > 0) {
          await new Promise(resolve => setTimeout(resolve, 500 * attempts));
        }
        
        // Get owner with timeout
        const owner = await Promise.race([
          vaultInstance.owner(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Timeout getting contract owner")), 5000)
          )
        ]);
        
        const isAdminUser = owner.toLowerCase() === userAddress.toLowerCase();
        
        console.log(`Admin check result: Owner=${owner}, User=${userAddress}, IsAdmin=${isAdminUser}`);
        
        setIsAdmin(isAdminUser);
        
        if (isAdminUser) {
          console.log("✅ Admin access confirmed");
          enqueueSnackbar("Admin access confirmed", { variant: "success" });
        }
        
        return isAdminUser;
        
      } catch (error) {
        attempts++;
        console.error(`Admin check attempt ${attempts} failed:`, error);
        
        if (attempts >= maxRetries) {
          console.error("❌ Admin check failed after all retries");
          setIsAdmin(false);
          enqueueSnackbar("Unable to verify admin status. Assuming regular user.", { variant: "warning" });
          return false;
        }
      }
    }
  }

  return (

    <Box sx={{ p: { xs: 1, sm: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} md={7} lg={6}>
          {!connected ? (
            <Card sx={{ mt: 4 }}>
              <CardContent>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Connect Your Wallet
                </Typography>
                <Button onClick={connectWallet} variant="contained" color="primary" size="large">
                  Connect Wallet
                </Button>
                {mintStatus && <Alert severity="info" sx={{ mt: 3 }}>{mintStatus}</Alert>}
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ mt: 4 }}>
              <CardContent>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Carbon Management Dashboard
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: 18 }}>
                    {account ? account.slice(2, 4).toUpperCase() : "?"}
                  </Avatar>
                  <Typography variant="body2" color="text.secondary">
                    {account}
                  </Typography>
                  {vaultStatus.verified && <Chip label="Verified" color="success" size="small" />}
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">Deposited</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {Number(vaultStatus.deposited) / 1e18
                        ? (Number(vaultStatus.deposited) / 1e18).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : "0.00"} CARBON
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">Active Mint</Typography>
                    <Typography variant="body1" fontWeight={600}
                      sx={{
                        color: (minting || vaultStatus.minting) ? 'error.main' : 'success.main'
                      }}                    
                    >
                      {minting || vaultStatus.minting ? "Yes" : "No"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">Verified</Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ color: vaultStatus.verified ? 'success.main' : 'error.main' }}
                    >
                      {vaultStatus.verified ? "Yes" : "No"}
                    </Typography>
                  </Grid>
                  {certificatePrice && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Current Certificate Price: {(Number(certificatePrice) / 1e18).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CARBON
                    </Alert>
                  )}
                </Grid>
                {paused && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    The Vault contract is currently <b>paused</b>. All user actions are temporarily disabled.
                  </Alert>
                )}

                <Divider sx={{ my: 3 }} />
                <TextField
                  label="Token amount"
                  value={depositAmount}
                  onChange={e => setDepositAmount(e.target.value)}
                  fullWidth
                  margin="dense"
                  size="small"
                  variant="outlined"
                />
                <Button onClick={depositTokens} 
                  variant="contained" 
                  color="primary" 
                  sx={{ mb: 2, mt: 1 }} 
                  fullWidth 
                  disabled={
                    paused ||
                    !depositAmount ||
                    isNaN(Number(depositAmount)) ||
                    Number(depositAmount) <= 0
                  }
                >
                  Deposit Tokens
                </Button>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Claim Refund always withdraws the full deposited vault balance and will set Verified to No.
                  Re-verification by admin is required before minting again.
                </Alert>
                <TextField
                  label="Refund amount (full vault balance only)"
                  value={refundAmount}
                  fullWidth
                  margin="dense"
                  size="small"
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                  helperText="Partial refunds are disabled on this screen."
                />
                <Button onClick={claimRefund} 
                  variant="contained" 
                  color="secondary" 
                  sx={{ mb: 2, mt: 1 }} 
                  fullWidth 
                  disabled={
                    paused ||
                    !vaultStatus?.deposited ||
                    String(vaultStatus.deposited) === "0"
                  }
                >
                  Claim Refund
                </Button>

                <Button
                  onClick={() => {
                    if (!vaultStatus.verified) {
                      setKycDialogOpen(true);
                    } else {
                      // V2-only flow: always show preview first; mint happens only via Confirm in the modal.
                      openV2BurnPlanPreview();
                    }
                  }}
                  disabled={
                    minting ||
                    paused
                  }
                  variant="contained"
                  color="success"
                  sx={{ mb: 2, mt: 2 }}
                  fullWidth
                >
                  {minting ? "Minting..." : "Preview & Mint (V2)"}
                </Button>

                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Switch
                      checked={true}
                      disabled
                      color="primary"
                    />
                  }
                  label="Use V2 provenance burn (messageIds) - always on"
                />

                {useV2Mint && (
                  <TextField
                    label="V2 Burn Plan (messageId:amount per line, or JSON)"
                    value={v2BurnPlanInput}
                    onChange={(e) => {
                      setV2BurnPlanInput(e.target.value);
                      setV2PlanApprovedSnapshot(null);
                    }}
                    fullWidth
                    margin="dense"
                    size="small"
                    multiline
                    minRows={3}
                    placeholder={
                      "Example lines:\n" +
                      "0x<64-hex-bytes32>:1000000000000000000\n" +
                      "0x<64-hex-bytes32>:2000000000000000000\n\n" +
                      "Or JSON: {\"messageIds\":[...],\"amounts\":[...]}"
                    }
                    helperText="V2 burns from your wallet (not deposited balance). Amounts can be wei integers or decimals in token units (18 decimals)."
                  />
                )}

                {useV2Mint && (
                  <Button
                    onClick={autoFillV2BurnPlanFromChain}
                    disabled={paused || minting}
                    variant="outlined"
                    sx={{ mb: 2, mt: 1 }}
                    fullWidth
                  >
                    Auto-fill burn plan from wallet lots
                  </Button>
                )}

                {useV2Mint && (
                  <Button
                    onClick={openV2BurnPlanPreview}
                    disabled={paused || minting}
                    variant="outlined"
                    sx={{ mb: 2, mt: 0 }}
                    fullWidth
                  >
                    Preview burn plan (V2)
                  </Button>
                )}


                <Dialog
                  open={v2PlanDialogOpen}
                  onClose={(event, reason) => {
                    if (reason === 'backdropClick') return;
                    setV2PlanDialogOpen(false);
                  }}
                  disableEscapeKeyDown
                  maxWidth="md"
                  fullWidth
                >
                  <DialogTitle>V2 Burn Plan: Manual Lot Selection</DialogTitle>
                  <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Select the lots and the number of NFTs you want to mint. Only the selected lots will be burned. The maximum number of NFTs depends on the total balance of the selected lots.
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">Number of NFTs to mint:</Typography>
                      <TextField
                        type="number"
                        size="small"
                        value={numNftsToMint}
                        onChange={e => {
                          let v = parseInt(e.target.value, 10);
                          if (isNaN(v) || v < 1) v = 1;
                          if (maxMintableNfts > 0 && v > maxMintableNfts) v = maxMintableNfts;
                          setNumNftsToMint(v);
                        }}
                        inputProps={{ min: 1, max: maxMintableNfts > 0 ? maxMintableNfts : undefined }}
                        helperText={`Maximum: ${maxMintableNfts}`}
                        sx={{ width: 120, mr: 2 }}
                      />
                    </Box>
                    <Typography variant="subtitle2">Your lots (for burn):</Typography>
                    {userLots.length === 0 ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        You have no available lots to burn. Please deposit or acquire lots first.
                      </Alert>
                    ) : (
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell></TableCell>
                              <TableCell>MessageId</TableCell>
                              <TableCell align="right">Balance (CARBON)</TableCell>
                              <TableCell align="right">Balance (wei)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {userLots.map((lot, idx) => (
                              <TableRow key={lot.messageId}>
                                <TableCell>
                                  <input
                                    type="checkbox"
                                    checked={selectedLots.includes(lot.messageId)}
                                    onChange={e => {
                                      if (e.target.checked) {
                                        setSelectedLots([...selectedLots, lot.messageId]);
                                      } else {
                                        setSelectedLots(selectedLots.filter(id => id !== lot.messageId));
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', maxWidth: 360, wordBreak: 'break-all' }}>{lot.messageId}</TableCell>
                                <TableCell align="right">{lot.balanceToken}</TableCell>
                                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{lot.balanceWei.toString()}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    <Button
                      variant="outlined"
                      onClick={openV2BurnPlanPreview}
                      disabled={userLots.length === 0 || !certificatePrice}
                      sx={{ mb: 2 }}
                    >
                      Preview burn plan for selected lots and NFT count
                    </Button>
                    {v2PlanPreview && (
                      <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Burn for {v2PlanPreview.numNfts} NFT(s): {formatUnitsRounded(v2PlanPreview.requiredBurnWei, 18, 5)} CARBON
                        </Alert>
                        <Typography variant="subtitle2" color="text.secondary">Provenance Root (preview)</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Computed as <span style={{ fontFamily: 'monospace' }}>keccak256(abi.encode(messageIds, amounts))</span>. This should match the on-chain event.
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 2 }}>
                          {v2PlanPreview.provenanceRootPreview}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Lots to burn</Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>MessageId</TableCell>
                                <TableCell align="right">Amount (CARBON)</TableCell>
                                <TableCell align="right">Amount (wei)</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {v2PlanPreview.rows.map((r, idx) => (
                                <TableRow key={idx}>
                                  <TableCell sx={{ fontFamily: 'monospace', maxWidth: 360, wordBreak: 'break-all' }}>{r.messageId}</TableCell>
                                  <TableCell align="right">{r.amountToken}</TableCell>
                                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{r.amountWei.toString()}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setV2PlanDialogOpen(false)}>Close</Button>
                    <Button
                      onClick={() => {
                        const snap = String(v2BurnPlanInput || "");
                        setV2PlanApprovedSnapshot(snap);
                        setV2PlanDialogOpen(false);
                        mintNFT({ v2ApprovedSnapshot: snap });
                      }}
                      disabled={!v2PlanPreview?.matches || minting || paused}
                      variant="contained"
                      color="success"
                    >
                      Confirm & Mint
                    </Button>
                  </DialogActions>
                </Dialog>


                <Dialog 
                  open={kycDialogOpen} 
                  onClose={() => setKycDialogOpen(false)}
                  maxWidth="md"
                  fullWidth
                >
                  <DialogTitle>KYC Verification Required</DialogTitle>
                  <DialogContent>
                    <KYCForm 
                      walletAddress={account}
                      onSubmissionComplete={handleKycSubmissionComplete}
                    />
                  </DialogContent>
                </Dialog>                

                <Dialog open={verifyDialogOpen} onClose={() => setVerifyDialogOpen(false)}>
                  <DialogTitle>{verifyAction ? "Verify User" : "Unverify User"}</DialogTitle>
                  <DialogContent>
                    <TextField
                      label="User Address"
                      value={verifyAddress}
                      onChange={e => setVerifyAddress(e.target.value)}
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      autoFocus
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setVerifyDialogOpen(false)} disabled={verifyLoading}>Cancel</Button>
                    <Button
                      onClick={handleVerifyDialogConfirm}
                      color={verifyAction ? "success" : "warning"}
                      variant="contained"
                      disabled={verifyLoading || !verifyAddress}
                    >
                      {verifyAction ? "Verify" : "Unverify"}
                    </Button>
                  </DialogActions>
                </Dialog>   


                {mintStatus && (
                  <Alert severity={mintStatus.toLowerCase().includes("fail") ? "error" : "info"} sx={{ mt: 2 }}>
                    {mintStatus}
                  </Alert>
                )}
                {mintedTokenId && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography fontWeight={700}>NFT Minted!</Typography>
                    <Typography variant="body2">Token ID: {mintedTokenId}</Typography>
                    <Typography variant="body2">
                      Token URI: 
                      {(() => {
                        const entry = mintedMetadataList.find(e => e.tokenId === mintedTokenId.toString());
                        if (entry && entry.uri) {
                          return (
                            <a
                              href={`https://serenity.ing/ipfs/${entry.uri.replace("ipfs://", "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "inherit",
                                textDecoration: "underline",
                                wordBreak: "break-all"
                              }}
                            >
                              {entry.uri}
                            </a>
                          );
                        }
                        return <span>null</span>;
                      })()}
                    </Typography>
                    <pre style={{ fontSize: 12, marginTop: 8, color: "green", background: "#f5f7fa", borderRadius: 6, padding: 8 }}>
                      {(() => {
                        const entry = mintedMetadataList.find(e => e.tokenId === mintedTokenId.toString());
                        return entry && entry.metadata ? JSON.stringify(entry.metadata, null, 2) : "";
                      })()}
                    </pre>
                  </Alert>
                )}
                {nftPreview && (
                  <Box className="nft-preview" sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 1 }}>
                    <Typography fontWeight={700}>NFT Preview</Typography>
                    {nftPreview.image && (
                      <img
                        src={nftPreview.image.replace("ipfs://", "https://serenity.ing/ipfs/")}
                        alt="nft"
                        className="nft-image-fixed"
                      />
                    )}
                    
                    {/* <pre style={{ fontSize: 12, marginTop: 8, background: "#f5f7fa", borderRadius: 6, padding: 8 }}>{JSON.stringify(nftPreview, null, 2)}</pre> */}
                    <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                      <QRCode value={mintedTokenURI} />
                    </Box>
                  </Box>
                )}
                {mintedMetadataList.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" fontWeight={600}>Your Minted & Purchased NFTs</Typography>
                    <Grid container spacing={2}>
                      {mintedMetadataList
                        .filter(entry => entry.isOwned)
                        .slice(nftPage * nftRowsPerPage, nftPage * nftRowsPerPage + nftRowsPerPage)
                        .map((entry, i) => {
                        let banner = null;
                        if (entry.isAuction) banner = "AUCTION";
                        else if (entry.isListed) banner = "LISTED";
                        else if (entry.isPurchased) banner = "PURCHASED";                        // else if (entry.isMinted) banner = "MINTED"; // Uncomment if you want a "MINTED" banner
                        else if (entry.isMinted && !entry.isOwned) banner = "SOLD"; // Minted but not owned

                        return (
                          <Grid item xs={12} sm={6} key={i}>
                            <Card className="minted-nft-card" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 1, position: "relative" }}>
                              {/* Banner */}
                              {banner && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 12,//12
                                    left: -24,//24
                                    background: banner === "LISTED"
                                      ? "linear-gradient(90deg, #1976d2 80%, #1565c0 100%)"
                                      : banner === "SOLD"
                                        ? "linear-gradient(90deg, #f3219f 80%, #f3219f 100%)"
                                        : "linear-gradient(90deg, #43a047 80%, #388e3c 100%)",
                                    color: "#fff",
                                    px: 3,
                                    py: 0.5,
                                    fontWeight: 700,
                                    fontSize: 14,
                                    borderRadius: "0 8px 8px 0",
                                    zIndex: 2,
                                    transform: "rotate(-45deg)",
                                    boxShadow: 2,
                                  }}
                                >
                                  {banner}
                                </Box>
                              )}
                              <Typography fontWeight={600}>Token ID: {entry.tokenId}</Typography>
                              {entry.metadata.image && (
                                <img src={entry.metadata.image.replace("ipfs://", "https://serenity.ing/ipfs/")} alt="NFT" className="nft-image-fixed" />
                              )}

                              <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                                <DialogTitle>Enter price in ETH</DialogTitle>
                                <DialogContent>
                                  <TextField
                                    autoFocus
                                    margin="dense"
                                    label="Price (ETH)"
                                    type="number"
                                    fullWidth
                                    value={priceInput}
                                    onChange={e => setPriceInput(e.target.value)}
                                  />
                                </DialogContent>
                                <DialogActions>
                                  <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                                  <Button
                                    onClick={handleDialogConfirm}
                                    variant="contained"
                                    color="primary"
                                    disabled={!priceInput}
                                  >
                                    List
                                  </Button>
                                </DialogActions>
                              </Dialog>


                              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Button
                                  onClick={() => showMetadataDialog(entry.metadata, entry.uri, entry.tokenId)}
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  sx={{ flex: 1, fontSize: '0.75rem', minWidth: 'auto' }}
                                >
                                  Metadata
                                </Button>
                                {entry.isListed ? (
                                  <Button
                                    onClick={() => onCancelListing(entry.tokenId)}
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    sx={{ flex: 1, fontSize: '0.75rem', minWidth: 'auto' }}
                                  >
                                    Cancel
                                  </Button>
                                ) : (
                                  <Button
                                    onClick={() => handleListClick(entry.tokenId)}
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    sx={{ 
                                      flex: 1, 
                                      fontSize: '0.75rem', 
                                      minWidth: 'auto',
                                      cursor: entry.retired ? 'not-allowed' : 'pointer',
                                      '&:disabled': {
                                        cursor: 'not-allowed'
                                      }
                                    }}
                                    disabled={((entry.isMinted && !entry.isOwned) || entry.retired)} // Add retired check
                                  >
                                    List
                                  </Button>
                                )}
                                {entry.isOwned && !entry.retired && !entry.isListed && (
                                  <Button
                                    onClick={() => retireNFT(entry.tokenId)}
                                    variant="contained"
                                    color="warning"
                                    size="small"
                                    sx={{ flex: 1, fontSize: '0.75rem', minWidth: 'auto' }}
                                  >
                                    Retire
                                  </Button>
                                )}
                              </Stack>

                              {entry.retired && (
                                <Alert severity="success" sx={{ mt: 1 }}>
                                  <Typography variant="body2" fontWeight={600} fontSize="0.75rem">
                                    ✅ RETIRED
                                  </Typography>
                                  <Typography variant="caption" fontSize="0.65rem">
                                    Retired by: {entry.retiredBy?.slice(0,6)}...{entry.retiredBy?.slice(-4)}
                                  </Typography>
                                  <Typography variant="caption" fontSize="0.65rem">
                                    Date: {entry.retiredTimestamp ? new Date(Number(entry.retiredTimestamp) * 1000).toLocaleDateString() : 'N/A'}
                                  </Typography>
                                </Alert>
                              )}

                              <Dialog open={metadataDialogOpen} onClose={handleCloseMetadataDialog} maxWidth="md" fullWidth>
                                <DialogTitle>Metadata JSON</DialogTitle>
                                <DialogContent>
                                  <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1, background: "#f7f9fc", border: "1px solid #e3e8ef" }}>
                                    <Typography variant="subtitle2" fontWeight={700}>Contributing Devices (V2)</Typography>
                                    {metadataDevicesLoading && (
                                      <Typography variant="body2" sx={{ mt: 0.75 }}>Resolving devices from burn lots...</Typography>
                                    )}
                                    {!metadataDevicesLoading && metadataDevicesError && (
                                      <Alert severity="warning" sx={{ mt: 1 }}>{metadataDevicesError}</Alert>
                                    )}
                                    {!metadataDevicesLoading && metadataDevicesReport?.available && (
                                      <>
                                        <Typography variant="caption" sx={{ display: "block", mt: 0.75 }}>
                                          Unique devices: {metadataDevicesReport.uniqueDevices} | Total burned: {metadataDevicesReport.totalToken} CARBON
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: "block", mt: 0.25 }}>
                                          Certificate primary device: {metadataDevicesReport.certificatePrimaryDevice || "n/a"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: "block", mt: 0.25 }}>
                                          Provenance check (metadata): {metadataDevicesReport.metadataRootMatches === null ? "n/a" : metadataDevicesReport.metadataRootMatches ? "OK" : "MISMATCH"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: "block", mt: 0.25 }}>
                                          Provenance check (chain payloadHash): {metadataDevicesReport.chainRootMatches === null ? "n/a" : metadataDevicesReport.chainRootMatches ? "OK" : "MISMATCH"}
                                        </Typography>

                                        <TableContainer component={Paper} sx={{ mt: 1, maxHeight: 220 }}>
                                          <Table size="small" stickyHeader>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Lots</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Total CARBON</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {metadataDevicesReport.groupedDevices.map((row) => (
                                                <TableRow key={row.device}>
                                                  <TableCell sx={{ fontSize: 12, wordBreak: "break-all" }}>{row.device}</TableCell>
                                                  <TableCell sx={{ fontSize: 12 }}>{row.lotCount}</TableCell>
                                                  <TableCell sx={{ fontSize: 12 }}>{row.totalToken}</TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </>
                                    )}
                                  </Box>
                                  <pre style={{ 
                                    whiteSpace: "pre-wrap",   
                                    wordBreak: "break-word",
                                    fontWeight: 900, 
                                    fontSize: 10, 
                                    color: "#222222", 
                                    background: "#f5f7fa", 
                                    borderRadius: 6, 
                                    padding: 8, 
                                    overflowX: "auto" }}>
                                    {metadataDialogContent ? JSON.stringify(metadataDialogContent, null, 2) : ""}
                                  </pre>
                                </DialogContent>
                                <DialogActions>
                                  <Button 
                                    onClick={handleDownloadImage}
                                    color="secondary"
                                    variant="contained"
                                    disabled={!metadataDialogContent || !metadataDialogContent.image}
                                  >
                                    Download Image
                                  </Button>                                  
                                  <Button onClick={handleCloseMetadataDialog} color="primary" variant="contained">
                                    Close
                                  </Button>
                                </DialogActions>
                              </Dialog>

                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <TablePagination
                        component="div"
                        count={mintedMetadataList.filter(entry => entry.isOwned).length}
                        page={nftPage}
                        onPageChange={(e, newPage) => setNftPage(newPage)}
                        rowsPerPage={nftRowsPerPage}
                        onRowsPerPageChange={e => {
                          setNftRowsPerPage(parseInt(e.target.value, 10));
                          setNftPage(0);
                        }}
                        rowsPerPageOptions={[3, 6, 12, 24]}
                        labelRowsPerPage="NFTs per page"
                      />
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          

          <Dialog open={listingDialogOpen} onClose={() => setListingDialogOpen(false)}>
            <DialogTitle>List NFT #{listingTokenId}</DialogTitle>
            <DialogContent>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="listing-type-label">Listing Type</InputLabel>
                <Select
                  labelId="listing-type-label"
                  value={listingType}
                  label="Listing Type"
                  onChange={e => setListingType(e.target.value)}
                >
                  <MenuItem value="fixed">Fixed Price</MenuItem>
                  <MenuItem value="auction">Auction</MenuItem>
                </Select>
              </FormControl>
              <TextField
                autoFocus
                margin="dense"
                label="Price (ETH)"
                type="number"
                fullWidth
                value={priceInput}
                onChange={e => setPriceInput(e.target.value)}
              />
              {listingType === "auction" && (
                <TextField
                  margin="dense"
                  label="Duration (minutes) - Min 60"
                  type="number"
                  fullWidth
                  value={durationInput}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setListingDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleListingDialogConfirm}
                variant="contained"
                color="primary"
                disabled={!priceInput || (listingType === "auction" && !durationInput)}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

        

        </Grid>
        {connected && isAdmin && (
          <Grid item xs={12}>
            <AdminDashboard 
              account={account}
              isAdmin={isAdmin}
              provider={provider}
              signer={signer}
              vault={vault}
              nft={nft}
              carbon={carbon}
              marketplace={marketplace}
              vaultAddress={VAULT_ADDRESS}
              nftAddress={NFT_ADDRESS}
              carbonAddress={CARBON_ADDRESS}
              marketplaceAddress={MARKETPLACE_ADDRESS}
              history={history}
              historyAll={historyAll}
              retiredEvents={retiredEvents}
              retiredEventsAll={retiredEventsAll}
              enqueueSnackbar={enqueueSnackbar}
              refreshVaultStatus={refreshVaultStatus}
            />
          </Grid>
        )}

        {/* COMMENTED OUT: Original basic admin panel - replaced with comprehensive AdminDashboard component above
        {connected && isAdmin && (
          <Grid item xs={12} md={5} lg={4}>
            <Card sx={{ mt: 4 }}>
              <CardContent>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Admin Dashboard
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Button 
                    onClick={async () => {
                      setVerifyLoading(true);
                      try {
                        await verifyUsers([account], true);
                        enqueueSnackbar("Self-verification successful!", { variant: "success" });
                        await refreshVaultStatus();
                      } catch (err) {
                        enqueueSnackbar("Self-verification failed: " + (err.reason || err.message), { variant: "error" });
                      }
                      setVerifyLoading(false);
                    }} 
                    variant="outlined" 
                    color="success"
                    disabled={verifyLoading}
                  >
                    {verifyLoading ? "Verifying..." : "Verify Self"}
                  </Button>
                  <Button 
                    onClick={async () => {
                      setVerifyLoading(true);
                      try {
                        await verifyUsers([account], false);
                        enqueueSnackbar("Self-unverification successful!", { variant: "success" });
                        await refreshVaultStatus();
                      } catch (err) {
                        enqueueSnackbar("Self-unverification failed: " + (err.reason || err.message), { variant: "error" });
                      }
                      setVerifyLoading(false);
                    }} 
                    variant="outlined" 
                    color="warning"
                    disabled={verifyLoading}
                  >
                    {verifyLoading ? "Unverifying..." : "Unverify Self"}
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    color="info"
                    onClick={() => { setVerifyDialogOpen(true); setVerifyAction(true); }}
                  >
                    Verify Users
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => { setVerifyDialogOpen(true); setVerifyAction(false); }}
                  >
                    Unverify Users
                  </Button>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Button onClick={pause} variant="outlined" color="secondary">Pause Dashboard</Button>
                  <Button onClick={unpause} variant="outlined" color="primary">Unpause Dashboard</Button>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Button onClick={pauseMarketplace} variant="outlined" color="secondary">Pause Marketplace</Button>
                  <Button onClick={unpauseMarketplace} variant="outlined" color="primary">Unpause Marketplace</Button>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Button onClick={() => setUpdateCarbonPriceDialogOpen(true)} variant="contained" color="info">
                    Update Carbon Price
                  </Button>
                  <Button onClick={() => setUpdateErgonPriceDialogOpen(true)} variant="contained" color="warning">
                    Update Ergon Price
                  </Button>
                </Stack>

                <Dialog open={updateCarbonPriceDialogOpen} onClose={() => setUpdateCarbonPriceDialogOpen(false)}>
                  <DialogTitle>Update Carbon Price</DialogTitle>
                  <DialogContent>
                    <TextField
                      autoFocus
                      margin="dense"
                      label="New Carbon Price"
                      type="number"
                      fullWidth
                      value={carbonPriceInput}
                      onChange={e => setCarbonPriceInput(e.target.value)}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setUpdateCarbonPriceDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleUpdateCarbonPriceConfirm} variant="contained" color="info" disabled={!carbonPriceInput}>
                      Confirm
                    </Button>
                  </DialogActions>
                </Dialog>

                <Dialog open={updateErgonPriceDialogOpen} onClose={() => setUpdateErgonPriceDialogOpen(false)}>
                  <DialogTitle>Update Ergon Price</DialogTitle>
                  <DialogContent>
                    <TextField
                      autoFocus
                      margin="dense"
                      label="New Ergon Price"
                      type="number"
                      fullWidth
                      value={ergonPriceInput}
                      onChange={e => setErgonPriceInput(e.target.value)}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setUpdateErgonPriceDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleUpdateErgonPriceConfirm} variant="contained" color="warning" disabled={!ergonPriceInput}>
                      Confirm
                    </Button>
                  </DialogActions>
                </Dialog>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={600}>Mint History</Typography>
                {history.length === 0 ? (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                    No mint events found
                  </Typography>
                ) : (
                  history.map((e, i) => (
                    <Typography key={i} variant="body2" sx={{ mt: 1 }}>
                      Minted Token #{e.args.tokenId.toString()} by {e.args.to.slice(0, 6)}...{e.args.to.slice(-4)}
                      {e.timestamp && ` on ${new Date(e.timestamp * 1000).toLocaleString()}`}
                      {e.args.burnedAmount && ` (Burned: ${e.args.burnedAmount.toString()})`}
                    </Typography>
                  ))
                )}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={600}>Retirement Events</Typography>
                {retiredEvents.length === 0 ? (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                    No retirement events found
                  </Typography>
                ) : (
                  retiredEvents.map((e, i) => (
                    <Typography key={i} variant="body2" sx={{ mt: 1 }}>
                      Token ID #{e.args.tokenId.toString()} retired by {e.args.retiredBy.slice(0, 6)}...{e.args.retiredBy.slice(-4)}
                      {e.timestamp && ` on ${new Date(e.timestamp * 1000).toLocaleString()}`}
                    </Typography>
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        END COMMENTED OUT: Original basic admin panel */}
      </Grid>
    </Box>
  );
}
